import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
    Box, Heading, Text, Tabs, TabList, Tab, TabPanels, TabPanel,
    Card, CardBody, CardHeader,
    Table, Thead, Tbody, Tr, Th, Td,
    Button, IconButton, Badge,
    Select, Input, Textarea,
    VStack, HStack, Flex, Spacer, Divider,
    SimpleGrid, Stat, StatLabel, StatNumber, StatHelpText,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    useDisclosure, useToast,
    AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
    AlertDialogContent, AlertDialogOverlay,
    Spinner, Tooltip, FormControl, FormLabel,
    NumberInput, NumberInputField,
    Alert, AlertIcon,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiRefreshCw, FiDownload, FiEye, FiCheck, FiUpload, FiLink, FiXCircle, FiMinus, FiPrinter, FiTrendingUp } from 'react-icons/fi';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';

const JOURNAL_LABELS = { BQ: 'Banque', CA: 'Caisse', VT: 'Ventes', OD: 'Op. Diverses' };
const JOURNAL_COLORS = { BQ: 'blue', CA: 'green', VT: 'purple', OD: 'gray' };

function formatMoney(v) {
    return parseFloat(v || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}

// Calcule le bilan actif/passif à partir de la balance générale d'un exercice
function computeBilan(annuelData) {
    const bal = annuelData.balance || [];
    const soldeClasse = (classe, sens) => bal
        .filter(b => b.classe === classe)
        .reduce((s, b) => {
            const d = parseFloat(b.total_debit), c = parseFloat(b.total_credit);
            return s + (sens === 'debiteur' ? d - c : c - d);
        }, 0);
    const actifImmobilise     = soldeClasse(2, 'debiteur');
    const actifCreances       = bal.filter(b => b.classe === 4 && (parseFloat(b.total_debit) - parseFloat(b.total_credit)) > 0)
        .reduce((s, b) => s + (parseFloat(b.total_debit) - parseFloat(b.total_credit)), 0);
    const actifDisponibilites = soldeClasse(5, 'debiteur');
    const totalActif = actifImmobilise + actifCreances + actifDisponibilites;

    const capitauxPropres  = soldeClasse(1, 'crediteur');
    const resultatExercice = annuelData.compte_resultat.resultat;
    const dettes = bal.filter(b => b.classe === 4 && (parseFloat(b.total_credit) - parseFloat(b.total_debit)) > 0)
        .reduce((s, b) => s + (parseFloat(b.total_credit) - parseFloat(b.total_debit)), 0);
    const totalPassif = capitauxPropres + resultatExercice + dettes;

    return { actifImmobilise, actifCreances, actifDisponibilites, totalActif, capitauxPropres, resultatExercice, dettes, totalPassif };
}

// ─────────────────────────────────────────────────────────────
// Document imprimable — rendu dans un portail hors de l'app pour
// garantir une mise en page A4 propre (logo, entête/pied répétés
// sur chaque page, sans scrollbar ni éléments de l'interface).
// ─────────────────────────────────────────────────────────────
function PrintableRapport({ annuelData }) {
    if (!annuelData) return null;
    const bilan = computeBilan(annuelData);
    const { exercice, compte_resultat: cr, balance } = annuelData;
    const money = formatMoney;

    return createPortal(
        <div id="rapport-print-portal">
            <style>{`
                #rapport-print-portal { display: none; }
                @media print {
                    @page { size: A4; margin: 16mm 14mm 20mm; }
                    body > *:not(#rapport-print-portal) { display: none !important; }
                    #rapport-print-portal { display: block !important; }
                }
                #rapport-print-portal, #rapport-print-portal * { box-sizing: border-box; }
                #rapport-print-portal {
                    font-family: -apple-system, 'Segoe UI', Arial, sans-serif;
                    color: #1a202c;
                    font-size: 10.5px;
                }
                #rapport-print-portal table.doc-shell { width: 100%; border-collapse: collapse; }
                #rapport-print-portal .letterhead {
                    display: flex; align-items: center; gap: 14px;
                    border-bottom: 2px solid #2c5282; padding-bottom: 10px; margin-bottom: 4px;
                }
                #rapport-print-portal .letterhead img { height: 52px; width: auto; }
                #rapport-print-portal .letterhead .titles { flex: 1; }
                #rapport-print-portal .letterhead h1 { font-size: 15px; margin: 0; color: #2c5282; letter-spacing: 0.3px; }
                #rapport-print-portal .letterhead .sub { font-size: 11px; color: #4a5568; margin-top: 2px; }
                #rapport-print-portal .letterhead .period { font-size: 9px; color: #718096; margin-top: 1px; }
                #rapport-print-portal .doc-footer {
                    border-top: 1px solid #cbd5e0; margin-top: 6px; padding-top: 6px;
                    font-size: 8px; color: #a0aec0; text-align: center; line-height: 1.5;
                }
                #rapport-print-portal .doc-footer .coords { color: #718096; }
                #rapport-print-portal .doc-footer .meta { margin-top: 2px; }
                #rapport-print-portal .spacer-head { height: 14px; }
                #rapport-print-portal .spacer-foot { height: 8px; }
                #rapport-print-portal h2.section {
                    font-size: 12px; color: #2d3748; margin: 16px 0 8px; padding-bottom: 4px;
                    border-bottom: 1px solid #e2e8f0;
                }
                #rapport-print-portal table.data { width: 100%; border-collapse: collapse; margin-bottom: 4px; }
                #rapport-print-portal table.data th {
                    text-align: left; font-size: 8.5px; text-transform: uppercase; letter-spacing: 0.3px;
                    color: #4a5568; background: #f7fafc; padding: 4px 6px; border: 1px solid #e2e8f0;
                }
                #rapport-print-portal table.data td { padding: 4px 6px; border: 1px solid #e2e8f0; font-size: 9.5px; }
                #rapport-print-portal table.data td.num, #rapport-print-portal table.data th.num { text-align: right; }
                #rapport-print-portal table.data tr.total td { font-weight: 700; background: #edf2f7; }
                #rapport-print-portal .cols-2 { display: flex; gap: 16px; }
                #rapport-print-portal .cols-2 > div { flex: 1; }
                #rapport-print-portal .cat-actif  h3 { color: #2b6cb0; }
                #rapport-print-portal .cat-passif h3 { color: #6b46c1; }
                #rapport-print-portal .cat-produits h3 { color: #2f855a; }
                #rapport-print-portal .cat-charges  h3 { color: #c53030; }
                #rapport-print-portal .cols-2 h3 { font-size: 10.5px; margin: 0 0 6px; }
                #rapport-print-portal .kpis { display: flex; gap: 10px; margin: 10px 0 4px; }
                #rapport-print-portal .kpi {
                    flex: 1; border: 1px solid #e2e8f0; border-radius: 4px; padding: 8px 10px;
                }
                #rapport-print-portal .kpi .lbl { font-size: 8.5px; color: #718096; text-transform: uppercase; }
                #rapport-print-portal .kpi .val { font-size: 13px; font-weight: 700; margin-top: 2px; }
                #rapport-print-portal .kpi.produits { border-left: 3px solid #38a169; }
                #rapport-print-portal .kpi.charges  { border-left: 3px solid #e53e3e; }
                #rapport-print-portal .kpi.resultat { border-left: 3px solid #3182ce; }
                #rapport-print-portal tr, table.data { page-break-inside: avoid; }
            `}</style>

            <table className="doc-shell">
                <thead>
                    <tr><td>
                        <div className="letterhead">
                            <img src="/logo-eecf.png" alt="Logo EECF" />
                            <div className="titles">
                                <h1>ÉGLISE ÉVANGÉLIQUE DU CAMEROUN EN FRANCE (EECF)</h1>
                                <div className="sub">Rapport Financier Annuel — Exercice {exercice.annee}</div>
                                <div className="period">Du {exercice.date_debut} au {exercice.date_fin}</div>
                            </div>
                        </div>
                        <div className="spacer-head" />
                    </td></tr>
                </thead>
                <tfoot>
                    <tr><td>
                        <div className="spacer-foot" />
                        <div className="doc-footer">
                            <div className="coords">
                                Église Évangélique du Cameroun en France (EECF) — Siège social : 38 rue Camille Desmoulins, 94230 Cachan
                                &nbsp;•&nbsp; accueil@eecf-paris.fr &nbsp;•&nbsp; +33 (0)6 63 59 70 04 &nbsp;•&nbsp; eecf-paris.fr
                            </div>
                            <div className="meta">
                                Document comptable interne — Généré le {new Date().toLocaleDateString('fr-FR')} — Exercice {exercice.annee}
                            </div>
                        </div>
                    </td></tr>
                </tfoot>
                <tbody>
                    <tr><td>
                        <h2 className="section">Bilan au 31/12/{exercice.annee}</h2>
                        <div className="cols-2">
                            <div className="cat-actif">
                                <h3>ACTIF</h3>
                                <table className="data">
                                    <thead><tr><th>Rubrique</th><th className="num">Montant</th></tr></thead>
                                    <tbody>
                                        <tr><td>Immobilisations</td><td className="num">{money(bilan.actifImmobilise)}</td></tr>
                                        <tr><td>Créances (adhérents, subventions...)</td><td className="num">{money(bilan.actifCreances)}</td></tr>
                                        <tr><td>Disponibilités (banque, caisse)</td><td className="num">{money(bilan.actifDisponibilites)}</td></tr>
                                        <tr className="total"><td>TOTAL ACTIF</td><td className="num">{money(bilan.totalActif)}</td></tr>
                                    </tbody>
                                </table>
                            </div>
                            <div className="cat-passif">
                                <h3>PASSIF</h3>
                                <table className="data">
                                    <thead><tr><th>Rubrique</th><th className="num">Montant</th></tr></thead>
                                    <tbody>
                                        <tr><td>Fonds associatifs / réserves</td><td className="num">{money(bilan.capitauxPropres)}</td></tr>
                                        <tr><td>Résultat de l'exercice</td><td className="num">{money(bilan.resultatExercice)}</td></tr>
                                        <tr><td>Dettes (fournisseurs, organismes...)</td><td className="num">{money(bilan.dettes)}</td></tr>
                                        <tr className="total"><td>TOTAL PASSIF</td><td className="num">{money(bilan.totalPassif)}</td></tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="kpis">
                            <div className="kpi produits"><div className="lbl">Total produits</div><div className="val">{money(cr.total_produits)}</div></div>
                            <div className="kpi charges"><div className="lbl">Total charges</div><div className="val">{money(cr.total_charges)}</div></div>
                            <div className="kpi resultat"><div className="lbl">Résultat {cr.resultat >= 0 ? '(excédent)' : '(déficit)'}</div>
                                <div className="val">{cr.resultat >= 0 ? '+' : ''}{money(cr.resultat)}</div></div>
                        </div>

                        <h2 className="section">Compte de Résultat</h2>
                        <div className="cols-2">
                            <div className="cat-produits">
                                <h3>PRODUITS</h3>
                                <table className="data">
                                    <thead><tr><th>Compte</th><th>Libellé</th><th className="num">Montant</th></tr></thead>
                                    <tbody>
                                        {cr.produits.map(p => (
                                            <tr key={p.numero}><td>{p.numero}</td><td>{p.libelle}</td><td className="num">{money(p.montant)}</td></tr>
                                        ))}
                                        <tr className="total"><td colSpan={2}>TOTAL PRODUITS</td><td className="num">{money(cr.total_produits)}</td></tr>
                                    </tbody>
                                </table>
                            </div>
                            <div className="cat-charges">
                                <h3>CHARGES</h3>
                                <table className="data">
                                    <thead><tr><th>Compte</th><th>Libellé</th><th className="num">Montant</th></tr></thead>
                                    <tbody>
                                        {cr.charges.map(c => (
                                            <tr key={c.numero}><td>{c.numero}</td><td>{c.libelle}</td><td className="num">{money(c.montant)}</td></tr>
                                        ))}
                                        <tr className="total"><td colSpan={2}>TOTAL CHARGES</td><td className="num">{money(cr.total_charges)}</td></tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <h2 className="section">Balance Générale</h2>
                        <table className="data">
                            <thead>
                                <tr><th>N°</th><th>Libellé</th><th className="num">Débit</th><th className="num">Crédit</th><th className="num">Solde débiteur</th><th className="num">Solde créditeur</th></tr>
                            </thead>
                            <tbody>
                                {balance.map(b => {
                                    const solde = parseFloat(b.total_debit) - parseFloat(b.total_credit);
                                    return (
                                        <tr key={b.numero}>
                                            <td>{b.numero}</td><td>{b.libelle}</td>
                                            <td className="num">{money(b.total_debit)}</td>
                                            <td className="num">{money(b.total_credit)}</td>
                                            <td className="num">{solde > 0 ? money(solde) : '–'}</td>
                                            <td className="num">{solde < 0 ? money(Math.abs(solde)) : '–'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </td></tr>
                </tbody>
            </table>
        </div>,
        document.body
    );
}

function ClasseBadge({ classe }) {
    const labels = { 1: 'Capitaux', 2: 'Immob.', 3: 'Stocks', 4: 'Tiers', 5: 'Financiers', 6: 'Charges', 7: 'Produits', 8: 'Asso.' };
    const colors = { 1: 'purple', 2: 'orange', 3: 'yellow', 4: 'cyan', 5: 'blue', 6: 'red', 7: 'green', 8: 'teal' };
    return <Badge colorScheme={colors[classe] || 'gray'} fontSize="xs">{labels[classe] || classe}</Badge>;
}

// ─────────────────────────────────────────────────────────────
// ONGLET BALANCE
// ─────────────────────────────────────────────────────────────
function BalanceTab({ exercices }) {
    const [exerciceId, setExerciceId] = useState('');
    const [balance, setBalance] = useState(null);
    const [loading, setLoading] = useState(false);
    const toast = useToast();

    useEffect(() => {
        if (exercices.length > 0 && !exerciceId) {
            const current = exercices.find(e => !e.is_cloture) || exercices[0];
            setExerciceId(String(current.id));
        }
    }, [exercices]); // eslint-disable-line

    const load = useCallback(async (id) => {
        if (!id) return;
        setLoading(true);
        try {
            const data = await apiService.makeRequest(`/comptabilite/balance?exercice_id=${id}`);
            setBalance(data.data);
        } catch (e) {
            toast({ title: 'Erreur', description: e.message, status: 'error', duration: 3000 });
        } finally { setLoading(false); }
    }, [toast]);

    useEffect(() => { if (exerciceId) load(exerciceId); }, [exerciceId, load]);

    return (
        <VStack align="stretch" spacing={4}>
            <HStack>
                <Select value={exerciceId} onChange={e => setExerciceId(e.target.value)} maxW="200px">
                    {exercices.map(ex => <option key={ex.id} value={ex.id}>{ex.annee}{ex.is_cloture ? ' (clôturé)' : ''}</option>)}
                </Select>
                <Button leftIcon={<FiRefreshCw />} onClick={() => load(exerciceId)} isLoading={loading} size="sm">Actualiser</Button>
            </HStack>

            {balance && (
                <>
                    {!balance.equilibre && (
                        <Alert status="error"><AlertIcon />Balance déséquilibrée ! Vérifiez les écritures.</Alert>
                    )}
                    {balance.equilibre && (
                        <Alert status="success"><AlertIcon />Balance équilibrée</Alert>
                    )}
                    <Box overflowX="auto">
                        <Table size="sm">
                            <Thead bg="gray.50">
                                <Tr>
                                    <Th>Compte</Th>
                                    <Th>Libellé</Th>
                                    <Th>Cl.</Th>
                                    <Th isNumeric>Débit total</Th>
                                    <Th isNumeric>Crédit total</Th>
                                    <Th isNumeric>Solde débiteur</Th>
                                    <Th isNumeric>Solde créditeur</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {balance.lignes.map(l => (
                                    <Tr key={l.numero} _hover={{ bg: 'gray.50' }}>
                                        <Td fontFamily="mono" fontSize="sm" fontWeight="600">{l.numero}</Td>
                                        <Td fontSize="sm">{l.libelle}</Td>
                                        <Td><ClasseBadge classe={l.classe} /></Td>
                                        <Td isNumeric fontSize="sm">{formatMoney(l.total_debit)}</Td>
                                        <Td isNumeric fontSize="sm">{formatMoney(l.total_credit)}</Td>
                                        <Td isNumeric fontSize="sm" color={parseFloat(l.solde_debiteur) > 0 ? 'blue.600' : 'gray.300'}>
                                            {parseFloat(l.solde_debiteur) > 0 ? formatMoney(l.solde_debiteur) : '-'}
                                        </Td>
                                        <Td isNumeric fontSize="sm" color={parseFloat(l.solde_crediteur) > 0 ? 'green.600' : 'gray.300'}>
                                            {parseFloat(l.solde_crediteur) > 0 ? formatMoney(l.solde_crediteur) : '-'}
                                        </Td>
                                    </Tr>
                                ))}
                                <Tr bg="gray.100" fontWeight="700">
                                    <Td colSpan={3}>TOTAUX</Td>
                                    <Td isNumeric>{formatMoney(balance.total_debit)}</Td>
                                    <Td isNumeric>{formatMoney(balance.total_credit)}</Td>
                                    <Td colSpan={2} />
                                </Tr>
                            </Tbody>
                        </Table>
                    </Box>
                </>
            )}
            {loading && <Flex justify="center"><Spinner /></Flex>}
        </VStack>
    );
}

// ─────────────────────────────────────────────────────────────
// ONGLET GRAND LIVRE
// ─────────────────────────────────────────────────────────────
function GrandLivreTab({ exercices, planComptable }) {
    const [exerciceId, setExerciceId] = useState('');
    const [compte, setCompte] = useState('');
    const [grandLivre, setGrandLivre] = useState([]);
    const [loading, setLoading] = useState(false);
    const toast = useToast();

    useEffect(() => {
        if (exercices.length > 0 && !exerciceId) {
            const current = exercices.find(e => !e.is_cloture) || exercices[0];
            setExerciceId(String(current.id));
        }
    }, [exercices]); // eslint-disable-line

    const load = async () => {
        if (!exerciceId) return;
        setLoading(true);
        try {
            const params = new URLSearchParams({ exercice_id: exerciceId });
            if (compte) params.append('compte', compte);
            const data = await apiService.makeRequest(`/comptabilite/grand-livre?${params}`);
            setGrandLivre(data.data);
        } catch (e) {
            toast({ title: 'Erreur', description: e.message, status: 'error', duration: 3000 });
        } finally { setLoading(false); }
    };

    useEffect(() => { if (exerciceId) load(); }, [exerciceId]); // eslint-disable-line

    return (
        <VStack align="stretch" spacing={4}>
            <HStack flexWrap="wrap">
                <Select value={exerciceId} onChange={e => setExerciceId(e.target.value)} maxW="200px">
                    {exercices.map(ex => <option key={ex.id} value={ex.id}>{ex.annee}{ex.is_cloture ? ' (clôturé)' : ''}</option>)}
                </Select>
                <Select value={compte} onChange={e => setCompte(e.target.value)} maxW="300px" placeholder="Tous les comptes">
                    {planComptable.map(pc => (
                        <option key={pc.numero} value={pc.numero}>{pc.numero} — {pc.libelle}</option>
                    ))}
                </Select>
                <Button leftIcon={<FiEye />} onClick={load} isLoading={loading} colorScheme="blue" size="sm">Afficher</Button>
            </HStack>

            {loading && <Flex justify="center"><Spinner /></Flex>}

            {grandLivre.map(c => (
                <Card key={c.numero} size="sm">
                    <CardHeader pb={2}>
                        <Flex align="center" gap={3}>
                            <Text fontFamily="mono" fontWeight="700" fontSize="lg">{c.numero}</Text>
                            <Text fontWeight="600">{c.libelle}</Text>
                            <ClasseBadge classe={c.classe} />
                            <Spacer />
                            <VStack spacing={0} align="end">
                                <Text fontSize="xs" color="gray.500">Débit : {formatMoney(c.total_debit)}</Text>
                                <Text fontSize="xs" color="gray.500">Crédit : {formatMoney(c.total_credit)}</Text>
                                <Text fontSize="sm" fontWeight="700" color={c.solde >= 0 ? 'blue.600' : 'red.600'}>
                                    Solde : {formatMoney(c.solde)}
                                </Text>
                            </VStack>
                        </Flex>
                    </CardHeader>
                    <CardBody pt={0}>
                        <Table size="xs" variant="simple">
                            <Thead><Tr><Th>Date</Th><Th>Libellé</Th><Th>Journal</Th><Th isNumeric>Débit</Th><Th isNumeric>Crédit</Th></Tr></Thead>
                            <Tbody>
                                {c.lignes.map((l, i) => (
                                    <Tr key={i} _hover={{ bg: 'gray.50' }}>
                                        <Td whiteSpace="nowrap" color="gray.600" fontSize="xs">{l.date}</Td>
                                        <Td fontSize="xs">{l.libelle}</Td>
                                        <Td><Badge colorScheme={JOURNAL_COLORS[l.journal] || 'gray'} fontSize="xx-small">{JOURNAL_LABELS[l.journal] || l.journal}</Badge></Td>
                                        <Td isNumeric fontSize="xs" color="blue.600">{l.debit > 0 ? formatMoney(l.debit) : '-'}</Td>
                                        <Td isNumeric fontSize="xs" color="green.600">{l.credit > 0 ? formatMoney(l.credit) : '-'}</Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </CardBody>
                </Card>
            ))}
            {!loading && grandLivre.length === 0 && (
                <Text color="gray.400" textAlign="center" py={8}>Aucune écriture pour cet exercice</Text>
            )}
        </VStack>
    );
}

// ─────────────────────────────────────────────────────────────
// ONGLET COMPTE DE RÉSULTAT
// ─────────────────────────────────────────────────────────────
function CompteResultatTab({ exercices }) {
    const [exerciceId, setExerciceId] = useState('');
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const toast = useToast();

    useEffect(() => {
        if (exercices.length > 0 && !exerciceId) {
            const current = exercices.find(e => !e.is_cloture) || exercices[0];
            setExerciceId(String(current.id));
        }
    }, [exercices]); // eslint-disable-line

    const load = useCallback(async (id) => {
        if (!id) return;
        setLoading(true);
        try {
            const data = await apiService.makeRequest(`/comptabilite/compte-resultat?exercice_id=${id}`);
            setResult(data.data);
        } catch (e) {
            toast({ title: 'Erreur', description: e.message, status: 'error', duration: 3000 });
        } finally { setLoading(false); }
    }, [toast]);

    useEffect(() => { if (exerciceId) load(exerciceId); }, [exerciceId, load]);

    return (
        <VStack align="stretch" spacing={4}>
            <HStack>
                <Select value={exerciceId} onChange={e => setExerciceId(e.target.value)} maxW="200px">
                    {exercices.map(ex => <option key={ex.id} value={ex.id}>{ex.annee}{ex.is_cloture ? ' (clôturé)' : ''}</option>)}
                </Select>
                <Button leftIcon={<FiRefreshCw />} onClick={() => load(exerciceId)} isLoading={loading} size="sm">Actualiser</Button>
            </HStack>

            {loading && <Flex justify="center"><Spinner /></Flex>}

            {result && (
                <>
                    {/* KPIs */}
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                        <Stat bg="green.50" p={4} borderRadius="md" border="1px solid" borderColor="green.200">
                            <StatLabel color="green.700">Total Produits</StatLabel>
                            <StatNumber color="green.700">{formatMoney(result.total_produits)}</StatNumber>
                        </Stat>
                        <Stat bg="red.50" p={4} borderRadius="md" border="1px solid" borderColor="red.200">
                            <StatLabel color="red.700">Total Charges</StatLabel>
                            <StatNumber color="red.700">{formatMoney(result.total_charges)}</StatNumber>
                        </Stat>
                        <Stat bg={result.excedent ? 'blue.50' : 'orange.50'} p={4} borderRadius="md" border="1px solid" borderColor={result.excedent ? 'blue.200' : 'orange.200'}>
                            <StatLabel color={result.excedent ? 'blue.700' : 'orange.700'}>
                                {result.excedent ? 'Excédent' : 'Déficit'}
                            </StatLabel>
                            <StatNumber color={result.excedent ? 'blue.700' : 'orange.700'}>{formatMoney(Math.abs(result.resultat))}</StatNumber>
                            <StatHelpText>{result.excedent ? 'Bénéfice de l\'exercice' : 'Perte de l\'exercice'}</StatHelpText>
                        </Stat>
                    </SimpleGrid>

                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                        {/* PRODUITS */}
                        <Card>
                            <CardHeader bg="green.50" borderTopRadius="md">
                                <Heading size="sm" color="green.700">Produits (Classe 7)</Heading>
                            </CardHeader>
                            <CardBody p={0}>
                                <Table size="sm">
                                    <Tbody>
                                        {result.produits.map(p => (
                                            <Tr key={p.numero}>
                                                <Td fontFamily="mono" fontSize="xs" color="gray.500" w="60px">{p.numero}</Td>
                                                <Td fontSize="sm">{p.libelle}</Td>
                                                <Td isNumeric fontSize="sm" fontWeight="600" color="green.600">{formatMoney(p.montant)}</Td>
                                            </Tr>
                                        ))}
                                        <Tr bg="green.100">
                                            <Td colSpan={2} fontWeight="700" fontSize="sm">TOTAL PRODUITS</Td>
                                            <Td isNumeric fontWeight="700" color="green.700">{formatMoney(result.total_produits)}</Td>
                                        </Tr>
                                    </Tbody>
                                </Table>
                            </CardBody>
                        </Card>

                        {/* CHARGES */}
                        <Card>
                            <CardHeader bg="red.50" borderTopRadius="md">
                                <Heading size="sm" color="red.700">Charges (Classe 6)</Heading>
                            </CardHeader>
                            <CardBody p={0}>
                                <Table size="sm">
                                    <Tbody>
                                        {result.charges.map(c => (
                                            <Tr key={c.numero}>
                                                <Td fontFamily="mono" fontSize="xs" color="gray.500" w="60px">{c.numero}</Td>
                                                <Td fontSize="sm">{c.libelle}</Td>
                                                <Td isNumeric fontSize="sm" fontWeight="600" color="red.600">{formatMoney(c.montant)}</Td>
                                            </Tr>
                                        ))}
                                        <Tr bg="red.100">
                                            <Td colSpan={2} fontWeight="700" fontSize="sm">TOTAL CHARGES</Td>
                                            <Td isNumeric fontWeight="700" color="red.700">{formatMoney(result.total_charges)}</Td>
                                        </Tr>
                                    </Tbody>
                                </Table>
                            </CardBody>
                        </Card>
                    </SimpleGrid>
                </>
            )}
        </VStack>
    );
}

// ─────────────────────────────────────────────────────────────
// ONGLET JOURNAL DES ECRITURES
// ─────────────────────────────────────────────────────────────
function JournalTab({ exercices, planComptable, currentUser }) {
    const [exerciceId, setExerciceId] = useState('');
    const [journalCode, setJournalCode] = useState('');
    const [ecritures, setEcritures] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [axes, setAxes] = useState([]);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const canWrite = ['admin', 'tresorier'].includes(currentUser?.role) || currentUser?.is_admin;

    useEffect(() => {
        if (exercices.length > 0 && !exerciceId) {
            const current = exercices.find(e => !e.is_cloture) || exercices[0];
            setExerciceId(String(current.id));
        }
    }, [exercices]); // eslint-disable-line

    const load = useCallback(async (p = 1) => {
        if (!exerciceId) return;
        setLoading(true);
        try {
            const params = new URLSearchParams({ exercice_id: exerciceId, page: p, limit: 30 });
            if (journalCode) params.append('journal_code', journalCode);
            const data = await apiService.makeRequest(`/comptabilite/ecritures?${params}`);
            setEcritures(data.data.ecritures);
            setTotal(data.data.total);
            setPage(p);
        } catch (e) {
            toast({ title: 'Erreur', description: e.message, status: 'error', duration: 3000 });
        } finally { setLoading(false); }
    }, [exerciceId, journalCode, toast]);

    useEffect(() => { if (exerciceId) load(1); }, [exerciceId]); // eslint-disable-line

    useEffect(() => {
        apiService.makeRequest('/comptabilite/analytique/axes')
            .then(d => setAxes(d.data.filter(a => a.is_active)))
            .catch(() => {});
    }, []);

    const assignAxe = async (ecritureId, axeId) => {
        try {
            await apiService.makeRequest(`/comptabilite/ecritures/${ecritureId}/axe`, {
                method: 'PUT', body: JSON.stringify({ axe_id: axeId || null })
            });
            setEcritures(prev => prev.map(e => e.id === ecritureId ? { ...e, axe_id: axeId || null } : e));
        } catch (err) {
            toast({ title: 'Erreur', description: err.message, status: 'error', duration: 3000 });
        }
    };

    const handleSync = async (type) => {
        setSyncing(true);
        try {
            const data = await apiService.makeRequest(`/comptabilite/sync-${type}`, {
                method: 'POST',
                body: JSON.stringify({ exercice_id: parseInt(exerciceId) })
            });
            toast({ title: 'Synchronisation', description: data.message, status: 'success', duration: 4000 });
            load(1);
        } catch (e) {
            toast({ title: 'Erreur sync', description: e.message, status: 'error', duration: 4000 });
        } finally { setSyncing(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Supprimer cette écriture ?')) return;
        try {
            await apiService.makeRequest(`/comptabilite/ecritures/${id}`, { method: 'DELETE' });
            toast({ title: 'Écriture supprimée', status: 'success', duration: 3000 });
            load(page);
        } catch (e) {
            toast({ title: 'Erreur', description: e.message, status: 'error', duration: 3000 });
        }
    };

    const totalPages = Math.ceil(total / 30);

    return (
        <VStack align="stretch" spacing={4}>
            <Flex gap={2} flexWrap="wrap" align="center">
                <Select value={exerciceId} onChange={e => setExerciceId(e.target.value)} maxW="180px">
                    {exercices.map(ex => <option key={ex.id} value={ex.id}>{ex.annee}{ex.is_cloture ? ' (clôturé)' : ''}</option>)}
                </Select>
                <Select value={journalCode} onChange={e => setJournalCode(e.target.value)} maxW="180px" placeholder="Tous les journaux">
                    {Object.entries(JOURNAL_LABELS).map(([k, v]) => <option key={k} value={k}>{k} — {v}</option>)}
                </Select>
                <Button size="sm" onClick={() => load(1)} isLoading={loading}>Filtrer</Button>
                <Spacer />
                {canWrite && (
                    <>
                        <Tooltip label="Générer les écritures des cotisations validées">
                            <Button size="sm" leftIcon={<FiRefreshCw />} colorScheme="teal" variant="outline" isLoading={syncing}
                                onClick={() => handleSync('cotisations')}>Sync cotisations</Button>
                        </Tooltip>
                        <Tooltip label="Générer les écritures des dépenses">
                            <Button size="sm" leftIcon={<FiRefreshCw />} colorScheme="orange" variant="outline" isLoading={syncing}
                                onClick={() => handleSync('depenses')}>Sync dépenses</Button>
                        </Tooltip>
                        <Tooltip label="Générer les écritures des offrandes et recettes du culte">
                            <Button size="sm" leftIcon={<FiRefreshCw />} colorScheme="purple" variant="outline" isLoading={syncing}
                                onClick={() => handleSync('culte')}>Sync culte</Button>
                        </Tooltip>
                        <Button size="sm" leftIcon={<FiPlus />} colorScheme="blue" onClick={onOpen}>Nouvelle écriture</Button>
                    </>
                )}
            </Flex>

            <Text fontSize="sm" color="gray.500">{total} écriture{total > 1 ? 's' : ''}</Text>

            {loading ? <Flex justify="center"><Spinner /></Flex> : (
                <Box overflowX="auto">
                    <Table size="sm">
                        <Thead bg="gray.50">
                            <Tr>
                                <Th>Date</Th>
                                <Th>Journal</Th>
                                <Th>Libellé</Th>
                                <Th>Source</Th>
                                <Th>Axe</Th>
                                <Th isNumeric>Débit</Th>
                                <Th isNumeric>Crédit</Th>
                                {canWrite && <Th />}
                            </Tr>
                        </Thead>
                        <Tbody>
                            {ecritures.length === 0 && (
                                <Tr><Td colSpan={7} textAlign="center" color="gray.400" py={8}>Aucune écriture</Td></Tr>
                            )}
                            {ecritures.map(e => {
                                const lignes = typeof e.lignes === 'string' ? JSON.parse(e.lignes) : (e.lignes || []);
                                const totalD = lignes.reduce((s, l) => s + parseFloat(l.debit || 0), 0);
                                const totalC = lignes.reduce((s, l) => s + parseFloat(l.credit || 0), 0);
                                return (
                                    <React.Fragment key={e.id}>
                                        <Tr bg="gray.50" _hover={{ bg: 'gray.100' }}>
                                            <Td fontWeight="600" fontSize="sm" whiteSpace="nowrap">{e.date_ecriture?.split('T')[0] || e.date_ecriture}</Td>
                                            <Td><Badge colorScheme={JOURNAL_COLORS[e.journal_code] || 'gray'}>{JOURNAL_LABELS[e.journal_code] || e.journal_code}</Badge></Td>
                                            <Td fontSize="sm">{e.libelle}</Td>
                                            <Td>
                                                {e.source_type !== 'manuel' && (
                                                    <Badge colorScheme={e.source_type === 'cotisation' ? 'green' : e.source_type === 'culte' ? 'purple' : 'orange'} fontSize="xx-small">
                                                        {e.source_type}
                                                    </Badge>
                                                )}
                                            </Td>
                                            <Td minW="120px">
                                                {canWrite ? (
                                                    <Select size="xs" value={e.axe_id || ''} onChange={ev => assignAxe(e.id, ev.target.value || null)} placeholder="–">
                                                        {axes.map(a => <option key={a.id} value={a.id}>{a.code}</option>)}
                                                    </Select>
                                                ) : (
                                                    e.axe_id ? <Badge fontSize="xx-small">{axes.find(a => a.id === e.axe_id)?.code || e.axe_id}</Badge> : <Text color="gray.300" fontSize="xs">–</Text>
                                                )}
                                            </Td>
                                            <Td isNumeric fontWeight="600" color="blue.600">{formatMoney(totalD)}</Td>
                                            <Td isNumeric fontWeight="600" color="green.600">{formatMoney(totalC)}</Td>
                                            {canWrite && (
                                                <Td>
                                                    {e.source_type === 'manuel' && (
                                                        <IconButton size="xs" icon={<FiTrash2 />} colorScheme="red" variant="ghost"
                                                            onClick={() => handleDelete(e.id)} aria-label="Supprimer" />
                                                    )}
                                                </Td>
                                            )}
                                        </Tr>
                                        {lignes.filter(l => l.compte_numero).map((l, i) => (
                                            <Tr key={i} fontSize="xs">
                                                <Td />
                                                <Td />
                                                <Td pl={8} color="gray.600">
                                                    <Text as="span" fontFamily="mono" color="gray.500">{l.compte_numero}</Text>
                                                    {' '}{l.compte_libelle || l.libelle}
                                                </Td>
                                                <Td />
                                                <Td isNumeric color="blue.500">{parseFloat(l.debit) > 0 ? formatMoney(l.debit) : ''}</Td>
                                                <Td isNumeric color="green.500">{parseFloat(l.credit) > 0 ? formatMoney(l.credit) : ''}</Td>
                                                {canWrite && <Td />}
                                            </Tr>
                                        ))}
                                    </React.Fragment>
                                );
                            })}
                        </Tbody>
                    </Table>
                </Box>
            )}

            {totalPages > 1 && (
                <Flex justify="center" gap={2}>
                    <Button size="sm" onClick={() => load(page - 1)} isDisabled={page <= 1}>Précédent</Button>
                    <Text alignSelf="center" fontSize="sm">Page {page} / {totalPages}</Text>
                    <Button size="sm" onClick={() => load(page + 1)} isDisabled={page >= totalPages}>Suivant</Button>
                </Flex>
            )}

            {canWrite && (
                <NewEcritureModal
                    isOpen={isOpen}
                    onClose={onClose}
                    exercices={exercices}
                    exerciceId={exerciceId}
                    planComptable={planComptable}
                    onSuccess={() => { onClose(); load(1); }}
                />
            )}
        </VStack>
    );
}

// ─────────────────────────────────────────────────────────────
// MODAL NOUVELLE ÉCRITURE
// ─────────────────────────────────────────────────────────────
function NewEcritureModal({ isOpen, onClose, exercices, exerciceId: defaultExerciceId, planComptable, onSuccess }) {
    const toast = useToast();
    const [loading, setLoading] = useState(false);
    const emptyLigne = { compte_numero: '', debit: '', credit: '' };
    const [form, setForm] = useState({
        exercice_id: defaultExerciceId,
        date_ecriture: new Date().toISOString().split('T')[0],
        libelle: '',
        piece_ref: '',
        journal_code: 'OD',
        lignes: [{ ...emptyLigne }, { ...emptyLigne }],
    });

    const totalDebit = form.lignes.reduce((s, l) => s + parseFloat(l.debit || 0), 0);
    const totalCredit = form.lignes.reduce((s, l) => s + parseFloat(l.credit || 0), 0);
    const equilibree = Math.abs(totalDebit - totalCredit) < 0.01;

    const updateLigne = (i, field, val) => {
        const lignes = [...form.lignes];
        lignes[i] = { ...lignes[i], [field]: val };
        setForm(f => ({ ...f, lignes }));
    };

    const addLigne = () => setForm(f => ({ ...f, lignes: [...f.lignes, { ...emptyLigne }] }));
    const removeLigne = (i) => setForm(f => ({ ...f, lignes: f.lignes.filter((_, idx) => idx !== i) }));

    const handleSubmit = async () => {
        if (!form.libelle.trim()) { toast({ title: 'Libellé requis', status: 'error', duration: 2000 }); return; }
        if (!equilibree) { toast({ title: 'Écriture déséquilibrée', description: `Débit ${totalDebit.toFixed(2)} ≠ Crédit ${totalCredit.toFixed(2)}`, status: 'error', duration: 3000 }); return; }
        if (form.lignes.some(l => !l.compte_numero)) { toast({ title: 'Compte manquant sur une ligne', status: 'error', duration: 2000 }); return; }

        setLoading(true);
        try {
            await apiService.makeRequest('/comptabilite/ecritures', {
                method: 'POST',
                body: JSON.stringify({
                    ...form,
                    exercice_id: parseInt(form.exercice_id),
                    lignes: form.lignes.map(l => ({
                        compte_numero: l.compte_numero,
                        debit: parseFloat(l.debit || 0),
                        credit: parseFloat(l.credit || 0),
                    })),
                }),
            });
            toast({ title: 'Écriture enregistrée', status: 'success', duration: 3000 });
            onSuccess();
        } catch (e) {
            toast({ title: 'Erreur', description: e.message, status: 'error', duration: 4000 });
        } finally { setLoading(false); }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Nouvelle écriture comptable</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack spacing={3}>
                        <SimpleGrid columns={2} spacing={3} w="full">
                            <FormControl isRequired>
                                <FormLabel fontSize="sm">Exercice</FormLabel>
                                <Select value={form.exercice_id} onChange={e => setForm(f => ({ ...f, exercice_id: e.target.value }))}>
                                    {exercices.filter(ex => !ex.is_cloture).map(ex => <option key={ex.id} value={ex.id}>{ex.annee}</option>)}
                                </Select>
                            </FormControl>
                            <FormControl isRequired>
                                <FormLabel fontSize="sm">Date</FormLabel>
                                <Input type="date" value={form.date_ecriture} onChange={e => setForm(f => ({ ...f, date_ecriture: e.target.value }))} />
                            </FormControl>
                        </SimpleGrid>
                        <SimpleGrid columns={2} spacing={3} w="full">
                            <FormControl isRequired>
                                <FormLabel fontSize="sm">Libellé</FormLabel>
                                <Input value={form.libelle} onChange={e => setForm(f => ({ ...f, libelle: e.target.value }))} placeholder="Description de l'écriture" />
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="sm">N° pièce</FormLabel>
                                <Input value={form.piece_ref} onChange={e => setForm(f => ({ ...f, piece_ref: e.target.value }))} placeholder="Facture, reçu…" />
                            </FormControl>
                        </SimpleGrid>
                        <FormControl>
                            <FormLabel fontSize="sm">Journal</FormLabel>
                            <Select value={form.journal_code} onChange={e => setForm(f => ({ ...f, journal_code: e.target.value }))} maxW="220px">
                                {Object.entries(JOURNAL_LABELS).map(([k, v]) => <option key={k} value={k}>{k} — {v}</option>)}
                            </Select>
                        </FormControl>

                        <Divider />
                        <Flex w="full" justify="space-between" align="center">
                            <Text fontWeight="600" fontSize="sm">Lignes d'écriture</Text>
                            <Button size="xs" leftIcon={<FiPlus />} onClick={addLigne}>Ajouter une ligne</Button>
                        </Flex>

                        <Box w="full" overflowX="auto">
                            <Table size="sm">
                                <Thead><Tr><Th>Compte</Th><Th isNumeric>Débit</Th><Th isNumeric>Crédit</Th><Th /></Tr></Thead>
                                <Tbody>
                                    {form.lignes.map((l, i) => (
                                        <Tr key={i}>
                                            <Td>
                                                <Select value={l.compte_numero} onChange={e => updateLigne(i, 'compte_numero', e.target.value)} size="sm" placeholder="Choisir…">
                                                    {planComptable.map(pc => (
                                                        <option key={pc.numero} value={pc.numero}>{pc.numero} — {pc.libelle}</option>
                                                    ))}
                                                </Select>
                                            </Td>
                                            <Td>
                                                <Input size="sm" type="number" step="0.01" min="0" value={l.debit}
                                                    onChange={e => updateLigne(i, 'debit', e.target.value)} placeholder="0.00" textAlign="right" />
                                            </Td>
                                            <Td>
                                                <Input size="sm" type="number" step="0.01" min="0" value={l.credit}
                                                    onChange={e => updateLigne(i, 'credit', e.target.value)} placeholder="0.00" textAlign="right" />
                                            </Td>
                                            <Td>
                                                {form.lignes.length > 2 && (
                                                    <IconButton size="xs" icon={<FiTrash2 />} variant="ghost" colorScheme="red" onClick={() => removeLigne(i)} aria-label="Supprimer" />
                                                )}
                                            </Td>
                                        </Tr>
                                    ))}
                                    <Tr bg={equilibree ? 'green.50' : 'red.50'}>
                                        <Td fontWeight="700" fontSize="sm">TOTAL</Td>
                                        <Td isNumeric fontWeight="700" color="blue.600">{formatMoney(totalDebit)}</Td>
                                        <Td isNumeric fontWeight="700" color="green.600">{formatMoney(totalCredit)}</Td>
                                        <Td>
                                            {equilibree
                                                ? <Badge colorScheme="green"><FiCheck style={{ display: 'inline' }} /> Équilibrée</Badge>
                                                : <Badge colorScheme="red">Déséquilibrée</Badge>
                                            }
                                        </Td>
                                    </Tr>
                                </Tbody>
                            </Table>
                        </Box>
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose}>Annuler</Button>
                    <Button colorScheme="blue" onClick={handleSubmit} isLoading={loading} isDisabled={!equilibree}>
                        Enregistrer
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

// ─────────────────────────────────────────────────────────────
// ONGLET ANALYTIQUE
// ─────────────────────────────────────────────────────────────
function AnalytiqueTab({ exercices, currentUser }) {
    const toast = useToast();
    const canWrite = currentUser?.is_admin || ['admin', 'tresorier'].includes(currentUser?.role);

    const [view, setView]           = useState('resultat'); // resultat | budget | axes
    const [exerciceId, setExerciceId] = useState('');
    const [axes, setAxes]           = useState([]);
    const [resultat, setResultat]   = useState(null);
    const [budgets, setBudgets]     = useState([]);
    const [planComptes, setPlanComptes] = useState([]);
    const [loading, setLoading]     = useState(false);

    // Modal axe
    const { isOpen: isAxeOpen, onOpen: onAxeOpen, onClose: onAxeClose } = useDisclosure();
    const [editAxe, setEditAxe]     = useState(null);
    const [axeForm, setAxeForm]     = useState({ code: '', libelle: '', description: '', couleur: '#3182CE' });

    // Modal budget
    const { isOpen: isBudgetOpen, onOpen: onBudgetOpen, onClose: onBudgetClose } = useDisclosure();
    const [budgetForm, setBudgetForm] = useState({ axe_id: '', compte_numero: '', montant_prevu: '' });

    useEffect(() => {
        if (exercices?.length > 0 && !exerciceId) {
            setExerciceId(String(exercices[exercices.length - 1].id));
        }
    }, [exercices, exerciceId]);

    const loadAxes = useCallback(async () => {
        try { const d = await apiService.makeRequest('/comptabilite/analytique/axes'); setAxes(d.data); }
        catch (e) { toast({ title: 'Erreur', description: e.message, status: 'error', duration: 3000 }); }
    }, [toast]);

    const loadResultat = useCallback(async (exId) => {
        if (!exId) return;
        setLoading(true);
        try { const d = await apiService.makeRequest(`/comptabilite/analytique/resultat?exercice_id=${exId}`); setResultat(d.data); }
        catch (e) { toast({ title: 'Erreur', description: e.message, status: 'error', duration: 3000 }); }
        finally { setLoading(false); }
    }, [toast]);

    const loadBudgets = useCallback(async (exId) => {
        if (!exId) return;
        try { const d = await apiService.makeRequest(`/comptabilite/analytique/budgets?exercice_id=${exId}`); setBudgets(d.data); }
        catch (e) { toast({ title: 'Erreur', description: e.message, status: 'error', duration: 3000 }); }
    }, [toast]);

    const loadPlan = useCallback(async () => {
        try { const d = await apiService.makeRequest('/comptabilite/plan'); setPlanComptes(d.data); }
        catch (e) {}
    }, []);

    useEffect(() => { loadAxes(); loadPlan(); }, [loadAxes, loadPlan]);
    useEffect(() => {
        if (!exerciceId) return;
        if (view === 'resultat') loadResultat(exerciceId);
        if (view === 'budget')   loadBudgets(exerciceId);
    }, [exerciceId, view, loadResultat, loadBudgets]);

    const saveAxe = async () => {
        try {
            if (editAxe) await apiService.makeRequest(`/comptabilite/analytique/axes/${editAxe.id}`, { method: 'PUT', body: JSON.stringify(axeForm) });
            else         await apiService.makeRequest('/comptabilite/analytique/axes', { method: 'POST', body: JSON.stringify(axeForm) });
            toast({ title: editAxe ? 'Axe mis à jour' : 'Axe créé', status: 'success', duration: 2000 });
            onAxeClose(); setEditAxe(null);
            setAxeForm({ code: '', libelle: '', description: '', couleur: '#3182CE' });
            loadAxes();
        } catch (e) { toast({ title: 'Erreur', description: e.message, status: 'error', duration: 3000 }); }
    };

    const saveBudget = async () => {
        try {
            await apiService.makeRequest('/comptabilite/analytique/budgets', {
                method: 'POST',
                body: JSON.stringify({ ...budgetForm, exercice_id: exerciceId })
            });
            toast({ title: 'Budget enregistré', status: 'success', duration: 2000 });
            onBudgetClose();
            setBudgetForm({ axe_id: '', compte_numero: '', montant_prevu: '' });
            loadBudgets(exerciceId);
        } catch (e) { toast({ title: 'Erreur', description: e.message, status: 'error', duration: 3000 }); }
    };

    // ── Barre de progression ──
    const ProgressBar = ({ value, max, color }) => {
        const pct = max > 0 ? Math.min(Math.round((value / max) * 100), 100) : 0;
        const over = max > 0 && value > max;
        return (
            <Box>
                <Box h="6px" bg="gray.100" borderRadius="full" overflow="hidden">
                    <Box h="6px" bg={over ? 'red.400' : color || 'blue.400'} borderRadius="full" w={`${pct}%`} transition="width 0.3s" />
                </Box>
                <Text fontSize="xx-small" color={over ? 'red.500' : 'gray.500'} mt="1px">{pct}%{over ? ' ⚠️ dépassé' : ''}</Text>
            </Box>
        );
    };

    // ── Vue Résultat par axe ──
    const renderResultat = () => {
        if (loading) return <Flex justify="center" py={10}><Spinner size="lg" /></Flex>;
        if (!resultat) return null;
        const { axes: axesData } = resultat;
        if (axesData.length === 0) return (
            <Alert status="info"><AlertIcon />Aucune écriture avec axe analytique. Affectez des axes aux écritures dans l'onglet Journal.</Alert>
        );
        return (
            <VStack align="stretch" spacing={4}>
                {axesData.map(a => (
                    <Card key={a.axe_id ?? 'none'} size="sm" borderLeft="4px solid" borderLeftColor={a.couleur}>
                        <CardHeader pb={1}>
                            <Flex justify="space-between" align="center">
                                <HStack>
                                    <Box w="10px" h="10px" borderRadius="full" bg={a.couleur} />
                                    <Heading size="sm">{a.axe_libelle}</Heading>
                                    {a.axe_code !== 'N/A' && <Badge fontFamily="mono" fontSize="xs">{a.axe_code}</Badge>}
                                </HStack>
                                <Badge colorScheme={a.resultat_realise >= 0 ? 'green' : 'red'} fontSize="sm">
                                    {a.resultat_realise >= 0 ? '+' : ''}{formatMoney(a.resultat_realise)}
                                </Badge>
                            </Flex>
                        </CardHeader>
                        <CardBody pt={0}>
                            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={3}>
                                <Box>
                                    <Text fontSize="xs" color="gray.500">Produits réalisés</Text>
                                    <Text fontWeight="600" color="green.600">{formatMoney(a.produits_realises)}</Text>
                                    {a.produits_prevus > 0 && (
                                        <>
                                            <Text fontSize="xs" color="gray.400">Budget : {formatMoney(a.produits_prevus)}</Text>
                                            <ProgressBar value={a.produits_realises} max={a.produits_prevus} color="green.400" />
                                        </>
                                    )}
                                </Box>
                                <Box>
                                    <Text fontSize="xs" color="gray.500">Charges réalisées</Text>
                                    <Text fontWeight="600" color="red.600">{formatMoney(a.charges_realisees)}</Text>
                                    {a.charges_prevues > 0 && (
                                        <>
                                            <Text fontSize="xs" color="gray.400">Budget : {formatMoney(a.charges_prevues)}</Text>
                                            <ProgressBar value={a.charges_realisees} max={a.charges_prevues} color="red.400" />
                                        </>
                                    )}
                                </Box>
                                <Box>
                                    <Text fontSize="xs" color="gray.500">Résultat budgété</Text>
                                    <Text fontWeight="600" color={a.resultat_prevu >= 0 ? 'blue.600' : 'orange.600'}>
                                        {a.resultat_prevu !== 0 ? (a.resultat_prevu >= 0 ? '+' : '') + formatMoney(a.resultat_prevu) : '–'}
                                    </Text>
                                </Box>
                                <Box>
                                    <Text fontSize="xs" color="gray.500">Écart</Text>
                                    {a.resultat_prevu !== 0 ? (
                                        <Text fontWeight="600" color={a.resultat_realise >= a.resultat_prevu ? 'green.600' : 'orange.600'}>
                                            {a.resultat_realise - a.resultat_prevu >= 0 ? '+' : ''}{formatMoney(a.resultat_realise - a.resultat_prevu)}
                                        </Text>
                                    ) : <Text color="gray.400" fontSize="sm">–</Text>}
                                </Box>
                            </SimpleGrid>

                            {/* Détail par compte */}
                            {a.lignes.length > 0 && (
                                <Box overflowX="auto">
                                    <Table size="xs" variant="simple">
                                        <Thead bg="gray.50">
                                            <Tr><Th>Compte</Th><Th>Libellé</Th><Th isNumeric>Réalisé</Th><Th isNumeric>Budget</Th><Th isNumeric>Écart</Th></Tr>
                                        </Thead>
                                        <Tbody>
                                            {a.lignes.map(l => {
                                                const ecart = (l.montant_realise || 0) - (l.montant_prevu || 0);
                                                return (
                                                    <Tr key={l.compte_numero}>
                                                        <Td fontFamily="mono" fontSize="xs"><Badge colorScheme={l.classe === 6 ? 'red' : 'green'} fontSize="xx-small">{l.compte_numero}</Badge></Td>
                                                        <Td fontSize="xs">{l.compte_libelle}</Td>
                                                        <Td isNumeric fontSize="xs" color={l.classe === 6 ? 'red.600' : 'green.600'}>{formatMoney(l.montant_realise || 0)}</Td>
                                                        <Td isNumeric fontSize="xs" color="gray.500">{l.montant_prevu ? formatMoney(l.montant_prevu) : '–'}</Td>
                                                        <Td isNumeric fontSize="xs" color={ecart >= 0 ? 'green.600' : 'red.600'}>{l.montant_prevu ? (ecart >= 0 ? '+' : '') + formatMoney(ecart) : '–'}</Td>
                                                    </Tr>
                                                );
                                            })}
                                        </Tbody>
                                    </Table>
                                </Box>
                            )}
                        </CardBody>
                    </Card>
                ))}
            </VStack>
        );
    };

    // ── Vue Budget ──
    const renderBudget = () => (
        <VStack align="stretch" spacing={4}>
            {canWrite && (
                <Flex justify="flex-end">
                    <Button leftIcon={<FiPlus />} colorScheme="blue" size="sm" onClick={onBudgetOpen}>Ajouter une ligne budget</Button>
                </Flex>
            )}
            <Box overflowX="auto">
                <Table size="sm">
                    <Thead bg="gray.50">
                        <Tr><Th>Axe</Th><Th>Compte</Th><Th>Libellé compte</Th><Th isNumeric>Montant prévu</Th>{canWrite && <Th />}</Tr>
                    </Thead>
                    <Tbody>
                        {budgets.length === 0 && <Tr><Td colSpan={5} textAlign="center" color="gray.400" py={6}>Aucun budget saisi pour cet exercice</Td></Tr>}
                        {budgets.map(b => (
                            <Tr key={b.id} _hover={{ bg: 'gray.50' }}>
                                <Td>
                                    <HStack spacing={1}>
                                        <Box w="8px" h="8px" borderRadius="full" bg={b.couleur} />
                                        <Text fontSize="xs">{b.axe_libelle}</Text>
                                    </HStack>
                                </Td>
                                <Td fontFamily="mono" fontSize="xs">{b.compte_numero}</Td>
                                <Td fontSize="xs">{b.compte_libelle}</Td>
                                <Td isNumeric fontSize="sm" fontWeight="600">{formatMoney(b.montant_prevu)}</Td>
                                {canWrite && (
                                    <Td>
                                        <IconButton size="xs" icon={<FiTrash2 />} variant="ghost" colorScheme="red"
                                            onClick={async () => { await apiService.makeRequest(`/comptabilite/analytique/budgets/${b.id}`, { method: 'DELETE' }); loadBudgets(exerciceId); }}
                                            aria-label="Supprimer" />
                                    </Td>
                                )}
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </Box>

            {/* Modal budget */}
            <Modal isOpen={isBudgetOpen} onClose={onBudgetClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Ajouter une ligne budget</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={3}>
                            <FormControl isRequired>
                                <FormLabel fontSize="sm">Axe analytique</FormLabel>
                                <Select value={budgetForm.axe_id} onChange={e => setBudgetForm(f => ({ ...f, axe_id: e.target.value }))} placeholder="Choisir un axe…">
                                    {axes.filter(a => a.is_active).map(a => <option key={a.id} value={a.id}>{a.libelle}</option>)}
                                </Select>
                            </FormControl>
                            <FormControl isRequired>
                                <FormLabel fontSize="sm">Compte PCG</FormLabel>
                                <Select value={budgetForm.compte_numero} onChange={e => setBudgetForm(f => ({ ...f, compte_numero: e.target.value }))} placeholder="Choisir un compte…">
                                    {planComptes.filter(p => [6, 7].includes(p.classe)).map(p => (
                                        <option key={p.numero} value={p.numero}>{p.numero} — {p.libelle}</option>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl isRequired>
                                <FormLabel fontSize="sm">Montant prévu (€)</FormLabel>
                                <Input type="number" step="0.01" value={budgetForm.montant_prevu}
                                    onChange={e => setBudgetForm(f => ({ ...f, montant_prevu: e.target.value }))} placeholder="0.00" />
                            </FormControl>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onBudgetClose}>Annuler</Button>
                        <Button colorScheme="blue" onClick={saveBudget}>Enregistrer</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </VStack>
    );

    // ── Vue Gestion des axes ──
    const renderAxes = () => (
        <VStack align="stretch" spacing={4}>
            {canWrite && (
                <Flex justify="flex-end">
                    <Button leftIcon={<FiPlus />} colorScheme="blue" size="sm"
                        onClick={() => { setEditAxe(null); setAxeForm({ code: '', libelle: '', description: '', couleur: '#3182CE' }); onAxeOpen(); }}>
                        Nouvel axe
                    </Button>
                </Flex>
            )}
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                {axes.map(a => (
                    <Card key={a.id} size="sm" borderLeft="4px solid" borderLeftColor={a.couleur} opacity={a.is_active ? 1 : 0.5}>
                        <CardBody>
                            <Flex justify="space-between" align="start">
                                <Box>
                                    <HStack mb={1}>
                                        <Box w="10px" h="10px" borderRadius="full" bg={a.couleur} />
                                        <Text fontWeight="700">{a.libelle}</Text>
                                    </HStack>
                                    <Badge fontFamily="mono" fontSize="xs" mb={1}>{a.code}</Badge>
                                    {a.description && <Text fontSize="xs" color="gray.500">{a.description}</Text>}
                                    {!a.is_active && <Badge colorScheme="gray" fontSize="xs">Inactif</Badge>}
                                </Box>
                                {canWrite && (
                                    <HStack>
                                        <IconButton size="xs" icon={<FiEye />} variant="ghost"
                                            onClick={() => { setEditAxe(a); setAxeForm({ code: a.code, libelle: a.libelle, description: a.description || '', couleur: a.couleur, is_active: a.is_active }); onAxeOpen(); }}
                                            aria-label="Modifier" />
                                        <IconButton size="xs" icon={<FiTrash2 />} variant="ghost" colorScheme="red"
                                            onClick={async () => { await apiService.makeRequest(`/comptabilite/analytique/axes/${a.id}`, { method: 'DELETE' }); loadAxes(); }}
                                            aria-label="Désactiver" />
                                    </HStack>
                                )}
                            </Flex>
                        </CardBody>
                    </Card>
                ))}
            </SimpleGrid>

            {/* Modal axe */}
            <Modal isOpen={isAxeOpen} onClose={onAxeClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{editAxe ? 'Modifier l\'axe' : 'Nouvel axe analytique'}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={3}>
                            <SimpleGrid columns={2} spacing={3} w="full">
                                <FormControl isRequired>
                                    <FormLabel fontSize="sm">Code</FormLabel>
                                    <Input value={axeForm.code} onChange={e => setAxeForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="CULTE" fontFamily="mono" disabled={!!editAxe} />
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="sm">Couleur</FormLabel>
                                    <Input type="color" value={axeForm.couleur} onChange={e => setAxeForm(f => ({ ...f, couleur: e.target.value }))} h="40px" p={1} />
                                </FormControl>
                            </SimpleGrid>
                            <FormControl isRequired>
                                <FormLabel fontSize="sm">Libellé</FormLabel>
                                <Input value={axeForm.libelle} onChange={e => setAxeForm(f => ({ ...f, libelle: e.target.value }))} placeholder="Culte et Adoration" />
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="sm">Description</FormLabel>
                                <Textarea value={axeForm.description} onChange={e => setAxeForm(f => ({ ...f, description: e.target.value }))} rows={2} />
                            </FormControl>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onAxeClose}>Annuler</Button>
                        <Button colorScheme="blue" onClick={saveAxe}>Enregistrer</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </VStack>
    );

    const exSelector = (
        <Select value={exerciceId} onChange={e => setExerciceId(e.target.value)} maxW="180px" size="sm">
            {exercices.map(ex => <option key={ex.id} value={ex.id}>{ex.annee}</option>)}
        </Select>
    );

    return (
        <VStack align="stretch" spacing={4}>
            <HStack spacing={1} borderBottom="2px solid" borderColor="gray.100" pb={2} flexWrap="wrap">
                <Button size="sm" variant={view === 'resultat' ? 'solid' : 'ghost'} colorScheme={view === 'resultat' ? 'blue' : 'gray'}
                    onClick={() => setView('resultat')}>📊 Résultat par axe</Button>
                <Button size="sm" variant={view === 'budget' ? 'solid' : 'ghost'} colorScheme={view === 'budget' ? 'blue' : 'gray'}
                    onClick={() => setView('budget')}>🎯 Budget prévisionnel</Button>
                <Button size="sm" variant={view === 'axes' ? 'solid' : 'ghost'} colorScheme={view === 'axes' ? 'blue' : 'gray'}
                    onClick={() => setView('axes')}>⚙️ Axes analytiques</Button>
                <Spacer />
                {view !== 'axes' && exSelector}
            </HStack>
            {view === 'resultat' && renderResultat()}
            {view === 'budget'   && renderBudget()}
            {view === 'axes'     && renderAxes()}
        </VStack>
    );
}

// ─────────────────────────────────────────────────────────────
// ONGLET RAPPORTS
// ─────────────────────────────────────────────────────────────
function RapportsTab({ exercices }) {
    const toast = useToast();
    const [view, setView]             = useState('pluriannuel'); // pluriannuel | annuel
    const [pluriData, setPluriData]   = useState(null);
    const [annuelData, setAnnuelData] = useState(null);
    const [loading, setLoading]       = useState(false);
    const [selectedEx, setSelectedEx] = useState('');

    // Chargement comparaison pluriannuelle
    const loadPluriannuel = useCallback(async () => {
        setLoading(true);
        try {
            const d = await apiService.makeRequest('/comptabilite/rapport-pluriannuel');
            setPluriData(d.data);
        } catch (e) {
            toast({ title: 'Erreur', description: e.message, status: 'error', duration: 3000 });
        } finally { setLoading(false); }
    }, [toast]);

    // Chargement rapport annuel
    const loadAnnuel = useCallback(async (exId) => {
        if (!exId) return;
        setLoading(true);
        try {
            const d = await apiService.makeRequest(`/comptabilite/rapport-annuel?exercice_id=${exId}`);
            setAnnuelData(d.data);
        } catch (e) {
            toast({ title: 'Erreur', description: e.message, status: 'error', duration: 3000 });
        } finally { setLoading(false); }
    }, [toast]);

    useEffect(() => {
        if (view === 'pluriannuel') loadPluriannuel();
    }, [view, loadPluriannuel]);

    useEffect(() => {
        if (exercices?.length > 0 && !selectedEx) {
            const latest = exercices[exercices.length - 1];
            setSelectedEx(String(latest.id));
        }
    }, [exercices, selectedEx]);

    // ── Comparaison pluriannuelle ──────────────────────────────
    const renderPluriannuel = () => {
        if (loading) return <Flex justify="center" py={10}><Spinner size="lg" /></Flex>;
        if (!pluriData) return null;
        const { results, lignes } = pluriData;
        if (results.length === 0) return <Text color="gray.400" py={6} textAlign="center">Aucun exercice avec des écritures.</Text>;

        const annees = results.map(r => r.exercice.annee);
        // Barre de progression relative au max
        const maxProduits = Math.max(...results.map(r => r.total_produits), 1);
        const maxCharges  = Math.max(...results.map(r => r.total_charges), 1);

        return (
            <VStack align="stretch" spacing={6}>
                {/* KPI synthèse par année */}
                <SimpleGrid columns={{ base: 1, sm: 2, lg: Math.min(results.length, 4) }} spacing={4}>
                    {results.map(r => (
                        <Card key={r.exercice.id} size="sm" border="1px solid" borderColor={r.resultat >= 0 ? 'green.200' : 'red.200'}>
                            <CardBody>
                                <Text fontWeight="700" fontSize="lg" mb={2}>{r.exercice.annee}</Text>
                                <Flex justify="space-between" mb={1}>
                                    <Text fontSize="xs" color="gray.500">Produits</Text>
                                    <Text fontSize="sm" fontWeight="600" color="green.600">{formatMoney(r.total_produits)}</Text>
                                </Flex>
                                {/* barre produits */}
                                <Box h="4px" bg="gray.100" borderRadius="full" mb={2}>
                                    <Box h="4px" bg="green.400" borderRadius="full" w={`${Math.round((r.total_produits / maxProduits) * 100)}%`} />
                                </Box>
                                <Flex justify="space-between" mb={1}>
                                    <Text fontSize="xs" color="gray.500">Charges</Text>
                                    <Text fontSize="sm" fontWeight="600" color="red.600">{formatMoney(r.total_charges)}</Text>
                                </Flex>
                                {/* barre charges */}
                                <Box h="4px" bg="gray.100" borderRadius="full" mb={3}>
                                    <Box h="4px" bg="red.400" borderRadius="full" w={`${Math.round((r.total_charges / maxCharges) * 100)}%`} />
                                </Box>
                                <Divider mb={2} />
                                <Flex justify="space-between" align="center">
                                    <Text fontSize="xs" color="gray.500">Résultat</Text>
                                    <Badge colorScheme={r.resultat >= 0 ? 'green' : 'red'} fontSize="sm" px={2}>
                                        {r.resultat >= 0 ? '+' : ''}{formatMoney(r.resultat)}
                                    </Badge>
                                </Flex>
                            </CardBody>
                        </Card>
                    ))}
                </SimpleGrid>

                {/* Tableau détaillé comptes */}
                {lignes.length > 0 && (
                    <Box overflowX="auto">
                        <Heading size="xs" mb={3} color="gray.600">Détail par compte</Heading>
                        <Table size="sm" variant="simple">
                            <Thead bg="gray.50">
                                <Tr>
                                    <Th>N°</Th>
                                    <Th>Libellé</Th>
                                    <Th>Classe</Th>
                                    {annees.map(a => <Th key={a} isNumeric>{a}</Th>)}
                                    {annees.length > 1 && <Th isNumeric>Évolution</Th>}
                                </Tr>
                            </Thead>
                            <Tbody>
                                {/* Sous-titre Produits */}
                                <Tr bg="green.50">
                                    <Td colSpan={3 + annees.length + (annees.length > 1 ? 1 : 0)}>
                                        <Text fontWeight="700" color="green.700" fontSize="xs">PRODUITS (Classe 7)</Text>
                                    </Td>
                                </Tr>
                                {lignes.filter(l => l.classe === 7).map(l => (
                                    <Tr key={l.numero} _hover={{ bg: 'gray.50' }}>
                                        <Td fontFamily="mono" fontSize="xs">{l.numero}</Td>
                                        <Td fontSize="xs">{l.libelle}</Td>
                                        <Td><Badge colorScheme="green" fontSize="xx-small">7</Badge></Td>
                                        {annees.map(a => (
                                            <Td key={a} isNumeric fontSize="xs" color={l.montants[a] > 0 ? 'green.700' : 'gray.400'}>
                                                {l.montants[a] ? formatMoney(l.montants[a]) : '–'}
                                            </Td>
                                        ))}
                                        {annees.length > 1 && (() => {
                                            const first = l.montants[annees[0]] || 0;
                                            const last  = l.montants[annees[annees.length - 1]] || 0;
                                            const evol  = first === 0 ? null : ((last - first) / Math.abs(first)) * 100;
                                            return (
                                                <Td isNumeric fontSize="xs">
                                                    {evol === null ? '–' : (
                                                        <Badge colorScheme={evol >= 0 ? 'green' : 'red'}>
                                                            {evol >= 0 ? '+' : ''}{evol.toFixed(1)}%
                                                        </Badge>
                                                    )}
                                                </Td>
                                            );
                                        })()}
                                    </Tr>
                                ))}
                                {/* Total produits */}
                                <Tr bg="green.50" fontWeight="700">
                                    <Td colSpan={3} fontSize="xs">TOTAL PRODUITS</Td>
                                    {annees.map(a => {
                                        const r = results.find(r => r.exercice.annee === a);
                                        return <Td key={a} isNumeric fontSize="xs" color="green.700">{formatMoney(r?.total_produits || 0)}</Td>;
                                    })}
                                    {annees.length > 1 && <Td />}
                                </Tr>

                                {/* Sous-titre Charges */}
                                <Tr bg="red.50">
                                    <Td colSpan={3 + annees.length + (annees.length > 1 ? 1 : 0)}>
                                        <Text fontWeight="700" color="red.700" fontSize="xs">CHARGES (Classe 6)</Text>
                                    </Td>
                                </Tr>
                                {lignes.filter(l => l.classe === 6).map(l => (
                                    <Tr key={l.numero} _hover={{ bg: 'gray.50' }}>
                                        <Td fontFamily="mono" fontSize="xs">{l.numero}</Td>
                                        <Td fontSize="xs">{l.libelle}</Td>
                                        <Td><Badge colorScheme="red" fontSize="xx-small">6</Badge></Td>
                                        {annees.map(a => (
                                            <Td key={a} isNumeric fontSize="xs" color={l.montants[a] > 0 ? 'red.600' : 'gray.400'}>
                                                {l.montants[a] ? formatMoney(l.montants[a]) : '–'}
                                            </Td>
                                        ))}
                                        {annees.length > 1 && (() => {
                                            const first = l.montants[annees[0]] || 0;
                                            const last  = l.montants[annees[annees.length - 1]] || 0;
                                            const evol  = first === 0 ? null : ((last - first) / Math.abs(first)) * 100;
                                            return (
                                                <Td isNumeric fontSize="xs">
                                                    {evol === null ? '–' : (
                                                        <Badge colorScheme={evol >= 0 ? 'red' : 'green'}>
                                                            {evol >= 0 ? '+' : ''}{evol.toFixed(1)}%
                                                        </Badge>
                                                    )}
                                                </Td>
                                            );
                                        })()}
                                    </Tr>
                                ))}
                                {/* Total charges */}
                                <Tr bg="red.50" fontWeight="700">
                                    <Td colSpan={3} fontSize="xs">TOTAL CHARGES</Td>
                                    {annees.map(a => {
                                        const r = results.find(r => r.exercice.annee === a);
                                        return <Td key={a} isNumeric fontSize="xs" color="red.700">{formatMoney(r?.total_charges || 0)}</Td>;
                                    })}
                                    {annees.length > 1 && <Td />}
                                </Tr>

                                {/* Résultat */}
                                <Tr bg="blue.50" fontWeight="700">
                                    <Td colSpan={3} fontSize="sm">RÉSULTAT DE L'EXERCICE</Td>
                                    {annees.map(a => {
                                        const r = results.find(r => r.exercice.annee === a);
                                        const res = r?.resultat || 0;
                                        return (
                                            <Td key={a} isNumeric>
                                                <Badge colorScheme={res >= 0 ? 'green' : 'red'} fontSize="sm">
                                                    {res >= 0 ? '+' : ''}{formatMoney(res)}
                                                </Badge>
                                            </Td>
                                        );
                                    })}
                                    {annees.length > 1 && (() => {
                                        const first = results[0]?.resultat || 0;
                                        const last  = results[results.length - 1]?.resultat || 0;
                                        const evol  = first === 0 ? null : ((last - first) / Math.abs(first)) * 100;
                                        return (
                                            <Td isNumeric>
                                                {evol === null ? '–' : (
                                                    <Badge colorScheme={evol >= 0 ? 'green' : 'red'}>
                                                        {evol >= 0 ? '+' : ''}{evol.toFixed(1)}%
                                                    </Badge>
                                                )}
                                            </Td>
                                        );
                                    })()}
                                </Tr>
                            </Tbody>
                        </Table>
                    </Box>
                )}
            </VStack>
        );
    };

    // ── Rapport annuel imprimable ──────────────────────────────
    const renderAnnuel = () => (
        <VStack align="stretch" spacing={4}>
            <HStack>
                <Select value={selectedEx} onChange={e => { setSelectedEx(e.target.value); loadAnnuel(e.target.value); }} maxW="200px">
                    {exercices.map(ex => <option key={ex.id} value={ex.id}>{ex.annee}</option>)}
                </Select>
                <Button leftIcon={<FiEye />} size="sm" colorScheme="blue" variant="outline"
                    onClick={() => loadAnnuel(selectedEx)} isLoading={loading}>
                    Charger
                </Button>
                {annuelData && (
                    <Button leftIcon={<FiPrinter />} size="sm" colorScheme="gray"
                        onClick={() => window.print()}>
                        Imprimer / PDF
                    </Button>
                )}
            </HStack>

            {loading && <Flex justify="center" py={10}><Spinner size="lg" /></Flex>}

            {/* Rendu réel envoyé à l'impression, hors de la mise en page de l'app */}
            <PrintableRapport annuelData={annuelData} />

            {annuelData && (() => {
                const { actifImmobilise, actifCreances, actifDisponibilites, totalActif,
                        capitauxPropres, resultatExercice, dettes, totalPassif } = computeBilan(annuelData);

                return (
                <Box className="rapport-annuel">
                    {/* En-tête */}
                    <Box textAlign="center" mb={6} pb={4} borderBottom="2px solid" borderColor="blue.200">
                        <Heading size="lg" color="blue.800">ÉGLISE ÉVANGÉLIQUE DU CAMEROUN EN FRANCE (EECF)</Heading>
                        <Text fontSize="md" color="gray.600" mt={1}>Rapport Financier Annuel — Exercice {annuelData.exercice.annee}</Text>
                        <Text fontSize="xs" color="gray.400" mt={1}>
                            Du {annuelData.exercice.date_debut} au {annuelData.exercice.date_fin}
                        </Text>
                    </Box>

                    {/* Bilan Actif / Passif */}
                    <Heading size="md" mb={3} color="gray.700">Bilan au 31/12/{annuelData.exercice.annee}</Heading>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={8}>
                        <Box>
                            <Text fontWeight="700" color="blue.700" mb={2} fontSize="sm">ACTIF</Text>
                            <Table size="sm" variant="simple">
                                <Thead bg="blue.50">
                                    <Tr><Th>Rubrique</Th><Th isNumeric>Montant</Th></Tr>
                                </Thead>
                                <Tbody>
                                    <Tr><Td fontSize="xs">Immobilisations</Td><Td isNumeric fontSize="xs">{formatMoney(actifImmobilise)}</Td></Tr>
                                    <Tr><Td fontSize="xs">Créances (adhérents, subventions à recevoir...)</Td><Td isNumeric fontSize="xs">{formatMoney(actifCreances)}</Td></Tr>
                                    <Tr><Td fontSize="xs">Disponibilités (banque, caisse)</Td><Td isNumeric fontSize="xs">{formatMoney(actifDisponibilites)}</Td></Tr>
                                    <Tr bg="blue.100" fontWeight="700">
                                        <Td fontSize="xs">TOTAL ACTIF</Td>
                                        <Td isNumeric fontSize="xs">{formatMoney(totalActif)}</Td>
                                    </Tr>
                                </Tbody>
                            </Table>
                        </Box>
                        <Box>
                            <Text fontWeight="700" color="purple.700" mb={2} fontSize="sm">PASSIF</Text>
                            <Table size="sm" variant="simple">
                                <Thead bg="purple.50">
                                    <Tr><Th>Rubrique</Th><Th isNumeric>Montant</Th></Tr>
                                </Thead>
                                <Tbody>
                                    <Tr><Td fontSize="xs">Fonds associatifs / réserves</Td><Td isNumeric fontSize="xs">{formatMoney(capitauxPropres)}</Td></Tr>
                                    <Tr><Td fontSize="xs">Résultat de l'exercice</Td><Td isNumeric fontSize="xs">{formatMoney(resultatExercice)}</Td></Tr>
                                    <Tr><Td fontSize="xs">Dettes (fournisseurs, organismes sociaux...)</Td><Td isNumeric fontSize="xs">{formatMoney(dettes)}</Td></Tr>
                                    <Tr bg="purple.100" fontWeight="700">
                                        <Td fontSize="xs">TOTAL PASSIF</Td>
                                        <Td isNumeric fontSize="xs">{formatMoney(totalPassif)}</Td>
                                    </Tr>
                                </Tbody>
                            </Table>
                        </Box>
                    </SimpleGrid>

                    {/* Synthèse */}
                    <SimpleGrid columns={3} spacing={4} mb={6}>
                        <Stat bg="green.50" p={4} borderRadius="md" border="1px solid" borderColor="green.200">
                            <StatLabel color="green.700">Total Produits</StatLabel>
                            <StatNumber color="green.700" fontSize="xl">{formatMoney(annuelData.compte_resultat.total_produits)}</StatNumber>
                        </Stat>
                        <Stat bg="red.50" p={4} borderRadius="md" border="1px solid" borderColor="red.200">
                            <StatLabel color="red.700">Total Charges</StatLabel>
                            <StatNumber color="red.700" fontSize="xl">{formatMoney(annuelData.compte_resultat.total_charges)}</StatNumber>
                        </Stat>
                        <Stat bg={annuelData.compte_resultat.resultat >= 0 ? 'blue.50' : 'orange.50'} p={4} borderRadius="md"
                            border="1px solid" borderColor={annuelData.compte_resultat.resultat >= 0 ? 'blue.200' : 'orange.200'}>
                            <StatLabel>Résultat</StatLabel>
                            <StatNumber fontSize="xl" color={annuelData.compte_resultat.resultat >= 0 ? 'blue.700' : 'orange.700'}>
                                {annuelData.compte_resultat.resultat >= 0 ? '+' : ''}{formatMoney(annuelData.compte_resultat.resultat)}
                            </StatNumber>
                            <StatHelpText>{annuelData.compte_resultat.resultat >= 0 ? '✅ Excédent' : '⚠️ Déficit'}</StatHelpText>
                        </Stat>
                    </SimpleGrid>

                    {/* Compte de résultat */}
                    <Heading size="md" mb={3} color="gray.700">Compte de Résultat</Heading>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
                        {/* Produits */}
                        <Box>
                            <Text fontWeight="700" color="green.700" mb={2} fontSize="sm">PRODUITS</Text>
                            <Table size="sm" variant="simple">
                                <Thead bg="green.50">
                                    <Tr><Th>Compte</Th><Th>Libellé</Th><Th isNumeric>Montant</Th></Tr>
                                </Thead>
                                <Tbody>
                                    {annuelData.compte_resultat.produits.map(p => (
                                        <Tr key={p.numero}>
                                            <Td fontFamily="mono" fontSize="xs">{p.numero}</Td>
                                            <Td fontSize="xs">{p.libelle}</Td>
                                            <Td isNumeric fontSize="xs" color="green.700">{formatMoney(p.montant)}</Td>
                                        </Tr>
                                    ))}
                                    <Tr bg="green.100" fontWeight="700">
                                        <Td colSpan={2} fontSize="xs">TOTAL PRODUITS</Td>
                                        <Td isNumeric fontSize="xs" color="green.700">{formatMoney(annuelData.compte_resultat.total_produits)}</Td>
                                    </Tr>
                                </Tbody>
                            </Table>
                        </Box>

                        {/* Charges */}
                        <Box>
                            <Text fontWeight="700" color="red.700" mb={2} fontSize="sm">CHARGES</Text>
                            <Table size="sm" variant="simple">
                                <Thead bg="red.50">
                                    <Tr><Th>Compte</Th><Th>Libellé</Th><Th isNumeric>Montant</Th></Tr>
                                </Thead>
                                <Tbody>
                                    {annuelData.compte_resultat.charges.map(c => (
                                        <Tr key={c.numero}>
                                            <Td fontFamily="mono" fontSize="xs">{c.numero}</Td>
                                            <Td fontSize="xs">{c.libelle}</Td>
                                            <Td isNumeric fontSize="xs" color="red.600">{formatMoney(c.montant)}</Td>
                                        </Tr>
                                    ))}
                                    <Tr bg="red.100" fontWeight="700">
                                        <Td colSpan={2} fontSize="xs">TOTAL CHARGES</Td>
                                        <Td isNumeric fontSize="xs" color="red.700">{formatMoney(annuelData.compte_resultat.total_charges)}</Td>
                                    </Tr>
                                </Tbody>
                            </Table>
                        </Box>
                    </SimpleGrid>

                    {/* Balance */}
                    <Box className="page-break">
                        <Heading size="md" mb={3} color="gray.700">Balance Générale</Heading>
                        <Box overflowX="auto">
                            <Table size="sm" variant="simple">
                                <Thead bg="gray.100">
                                    <Tr>
                                        <Th>N°</Th><Th>Libellé</Th>
                                        <Th isNumeric>Total Débit</Th><Th isNumeric>Total Crédit</Th><Th isNumeric>Solde Débiteur</Th><Th isNumeric>Solde Créditeur</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {annuelData.balance.map(b => {
                                        const solde = parseFloat(b.total_debit) - parseFloat(b.total_credit);
                                        return (
                                            <Tr key={b.numero} _hover={{ bg: 'gray.50' }}>
                                                <Td fontFamily="mono" fontSize="xs">{b.numero}</Td>
                                                <Td fontSize="xs">{b.libelle}</Td>
                                                <Td isNumeric fontSize="xs">{formatMoney(b.total_debit)}</Td>
                                                <Td isNumeric fontSize="xs">{formatMoney(b.total_credit)}</Td>
                                                <Td isNumeric fontSize="xs" color={solde > 0 ? 'blue.600' : 'gray.400'}>{solde > 0 ? formatMoney(solde) : '–'}</Td>
                                                <Td isNumeric fontSize="xs" color={solde < 0 ? 'orange.600' : 'gray.400'}>{solde < 0 ? formatMoney(Math.abs(solde)) : '–'}</Td>
                                            </Tr>
                                        );
                                    })}
                                </Tbody>
                            </Table>
                        </Box>
                    </Box>

                    {/* Pied de rapport */}
                    <Box mt={8} pt={4} borderTop="1px solid" borderColor="gray.200" textAlign="center">
                        <Text fontSize="xs" color="gray.400">
                            Rapport généré le {new Date().toLocaleDateString('fr-FR')} — Exercice {annuelData.exercice.annee}
                        </Text>
                    </Box>
                </Box>
                );
            })()}
        </VStack>
    );

    return (
        <VStack align="stretch" spacing={4}>
            <HStack spacing={1} borderBottom="2px solid" borderColor="gray.100" pb={2}>
                <Button size="sm" variant={view === 'pluriannuel' ? 'solid' : 'ghost'}
                    colorScheme={view === 'pluriannuel' ? 'blue' : 'gray'}
                    leftIcon={<FiTrendingUp />}
                    onClick={() => setView('pluriannuel')}>
                    Comparaison pluriannuelle
                </Button>
                <Button size="sm" variant={view === 'annuel' ? 'solid' : 'ghost'}
                    colorScheme={view === 'annuel' ? 'blue' : 'gray'}
                    leftIcon={<FiPrinter />}
                    onClick={() => { setView('annuel'); if (selectedEx) loadAnnuel(selectedEx); }}>
                    Rapport annuel imprimable
                </Button>
            </HStack>
            {view === 'pluriannuel' && renderPluriannuel()}
            {view === 'annuel'      && renderAnnuel()}
        </VStack>
    );
}

// ─────────────────────────────────────────────────────────────
// ONGLET BANQUE
// ─────────────────────────────────────────────────────────────
function BanqueTab({ currentUser }) {
    const toast = useToast();
    const canWrite = currentUser?.is_admin || ['admin', 'tresorier'].includes(currentUser?.role);
    // Le commissaire aux comptes fait le contrôle bancaire (import des relevés et rapprochement), mais ne gère ni les comptes ni la suppression des relevés
    const canRapprocher = canWrite || currentUser?.role === 'commissaire';

    // ── Sections internes ──
    const [section, setSection] = useState('comptes'); // comptes | import | rapprochement

    // ── Comptes ──
    const [comptes, setComptes] = useState([]);
    const [loadingComptes, setLoadingComptes] = useState(false);
    const { isOpen: isCompteOpen, onOpen: onCompteOpen, onClose: onCompteClose } = useDisclosure();
    const [editCompte, setEditCompte] = useState(null);
    const [compteForm, setCompteForm] = useState({ nom: '', banque: '', iban: '', compte_comptable: '512', solde_initial: '', date_ouverture: '' });

    // ── Relevés ──
    const [releves, setReleves] = useState([]);
    const [selectedCompteId, setSelectedCompteId] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadForm, setUploadForm] = useState({ compte_id: '', solde_initial: '', solde_final: '' });
    const fileRef = React.useRef();

    // ── Rapprochement ──
    const [selectedReleveId, setSelectedReleveId] = useState('');
    const [rapprochement, setRapprochement] = useState(null);
    const [loadingRap, setLoadingRap] = useState(false);
    const [resume, setResume] = useState(null);
    const [selectedLigneId, setSelectedLigneId] = useState(null);

    const loadComptes = useCallback(async () => {
        setLoadingComptes(true);
        try {
            const d = await apiService.makeRequest('/banque/comptes');
            setComptes(d.data);
        } catch (e) {
            toast({ title: 'Erreur', description: e.message, status: 'error', duration: 3000 });
        } finally { setLoadingComptes(false); }
    }, [toast]);

    const loadReleves = useCallback(async (compteId) => {
        try {
            const params = compteId ? `?compte_id=${compteId}` : '';
            const d = await apiService.makeRequest(`/banque/releves${params}`);
            setReleves(d.data);
        } catch (e) {
            toast({ title: 'Erreur', description: e.message, status: 'error', duration: 3000 });
        }
    }, [toast]);

    useEffect(() => { loadComptes(); }, [loadComptes]);
    useEffect(() => { if (section === 'import' || section === 'rapprochement') loadReleves(selectedCompteId); }, [section, selectedCompteId, loadReleves]);

    // ── Sauvegarde compte ──
    const saveCompte = async () => {
        try {
            if (editCompte) {
                await apiService.makeRequest(`/banque/comptes/${editCompte.id}`, { method: 'PUT', body: JSON.stringify(compteForm) });
            } else {
                await apiService.makeRequest('/banque/comptes', { method: 'POST', body: JSON.stringify(compteForm) });
            }
            toast({ title: editCompte ? 'Compte mis à jour' : 'Compte créé', status: 'success', duration: 3000 });
            onCompteClose();
            setEditCompte(null);
            setCompteForm({ nom: '', banque: '', iban: '', compte_comptable: '512', solde_initial: '', date_ouverture: '' });
            loadComptes();
        } catch (e) {
            toast({ title: 'Erreur', description: e.message, status: 'error', duration: 3000 });
        }
    };

    // ── Import CSV ──
    const handleImport = async () => {
        if (!fileRef.current?.files[0]) { toast({ title: 'Sélectionnez un fichier CSV', status: 'warning', duration: 2000 }); return; }
        if (!uploadForm.compte_id) { toast({ title: 'Sélectionnez un compte bancaire', status: 'warning', duration: 2000 }); return; }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('fichier', fileRef.current.files[0]);
            fd.append('compte_id', uploadForm.compte_id);
            if (uploadForm.solde_initial) fd.append('solde_initial', uploadForm.solde_initial);
            if (uploadForm.solde_final)   fd.append('solde_final',   uploadForm.solde_final);

            const data = await apiService.makeRequest('/banque/releves/import', { method: 'POST', body: fd });
            toast({ title: 'Import réussi', description: data.message, status: 'success', duration: 4000 });
            fileRef.current.value = '';
            loadReleves(uploadForm.compte_id);
            loadComptes();
        } catch (e) {
            toast({ title: 'Erreur import', description: e.message, status: 'error', duration: 4000 });
        } finally { setUploading(false); }
    };

    // ── Rapprochement ──
    const loadRapprochement = async (releveId) => {
        if (!releveId) return;
        setLoadingRap(true);
        try {
            const [rapData, resumeData] = await Promise.all([
                apiService.makeRequest(`/banque/rapprochement/suggestions?releve_id=${releveId}`),
                apiService.makeRequest(`/banque/rapprochement/resume?releve_id=${releveId}`),
            ]);
            setRapprochement(rapData.data);
            setResume(resumeData.data);
        } catch (e) {
            toast({ title: 'Erreur', description: e.message, status: 'error', duration: 3000 });
        } finally { setLoadingRap(false); }
    };

    const lettrer = async (ligneId, ecritureId) => {
        try {
            await apiService.makeRequest('/banque/rapprochement/lettrer', { method: 'POST', body: JSON.stringify({ ligne_id: ligneId, ecriture_id: ecritureId }) });
            setSelectedLigneId(null);
            loadRapprochement(selectedReleveId);
        } catch (e) {
            toast({ title: 'Erreur', description: e.message, status: 'error', duration: 3000 });
        }
    };

    const delettrer = async (ligneId) => {
        try {
            await apiService.makeRequest('/banque/rapprochement/delettrer', { method: 'POST', body: JSON.stringify({ ligne_id: ligneId }) });
            loadRapprochement(selectedReleveId);
        } catch (e) {
            toast({ title: 'Erreur', description: e.message, status: 'error', duration: 3000 });
        }
    };

    const ignorer = async (ligneId) => {
        try {
            await apiService.makeRequest('/banque/rapprochement/ignorer', { method: 'POST', body: JSON.stringify({ ligne_id: ligneId }) });
            loadRapprochement(selectedReleveId);
        } catch (e) {
            toast({ title: 'Erreur', description: e.message, status: 'error', duration: 3000 });
        }
    };

    // ── Rendu par section ──
    const renderComptes = () => (
        <VStack align="stretch" spacing={4}>
            {canWrite && (
                <Flex justify="flex-end">
                    <Button leftIcon={<FiPlus />} colorScheme="blue" size="sm" onClick={() => { setEditCompte(null); setCompteForm({ nom: '', banque: '', iban: '', compte_comptable: '512', solde_initial: '', date_ouverture: '' }); onCompteOpen(); }}>
                        Nouveau compte
                    </Button>
                </Flex>
            )}
            {loadingComptes ? <Flex justify="center"><Spinner /></Flex> : (
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                    {comptes.length === 0 && <Text color="gray.400">Aucun compte bancaire. Créez-en un pour commencer.</Text>}
                    {comptes.map(c => (
                        <Card key={c.id} size="sm" border="1px solid" borderColor="gray.200">
                            <CardBody>
                                <Flex align="start" justify="space-between">
                                    <Box>
                                        <Text fontWeight="700" fontSize="md">{c.nom}</Text>
                                        {c.banque && <Text fontSize="xs" color="gray.500">{c.banque}</Text>}
                                        {c.iban && <Text fontSize="xs" color="gray.400" fontFamily="mono">{c.iban}</Text>}
                                    </Box>
                                    {canWrite && (
                                        <HStack>
                                            <IconButton size="xs" icon={<FiEye />} variant="ghost"
                                                onClick={() => { setEditCompte(c); setCompteForm({ nom: c.nom, banque: c.banque || '', iban: c.iban || '', compte_comptable: c.compte_comptable, solde_initial: c.solde_initial, date_ouverture: c.date_ouverture?.split('T')[0] || '' }); onCompteOpen(); }}
                                                aria-label="Modifier" />
                                            <IconButton size="xs" icon={<FiTrash2 />} variant="ghost" colorScheme="red"
                                                onClick={async () => { if (window.confirm('Désactiver ce compte ?')) { await apiService.makeRequest(`/banque/comptes/${c.id}`, { method: 'DELETE' }); loadComptes(); } }}
                                                aria-label="Supprimer" />
                                        </HStack>
                                    )}
                                </Flex>
                                <Divider my={3} />
                                <Flex justify="space-between">
                                    <Text fontSize="xs" color="gray.500">Solde actuel</Text>
                                    <Text fontWeight="700" fontSize="lg" color={parseFloat(c.solde_actuel) >= 0 ? 'green.600' : 'red.600'}>
                                        {formatMoney(c.solde_actuel)}
                                    </Text>
                                </Flex>
                                <Flex justify="space-between" mt={1}>
                                    <Text fontSize="xs" color="gray.400">Compte PCG</Text>
                                    <Badge fontFamily="mono">{c.compte_comptable}</Badge>
                                </Flex>
                                <Flex justify="space-between" mt={1}>
                                    <Text fontSize="xs" color="gray.400">Relevés importés</Text>
                                    <Badge>{c.nb_releves}</Badge>
                                </Flex>
                            </CardBody>
                        </Card>
                    ))}
                </SimpleGrid>
            )}

            {/* Modal compte */}
            <Modal isOpen={isCompteOpen} onClose={onCompteClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{editCompte ? 'Modifier le compte' : 'Nouveau compte bancaire'}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={3}>
                            <FormControl isRequired>
                                <FormLabel fontSize="sm">Nom du compte</FormLabel>
                                <Input value={compteForm.nom} onChange={e => setCompteForm(f => ({ ...f, nom: e.target.value }))} placeholder="Ex: CCP EECF" />
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="sm">Banque</FormLabel>
                                <Input value={compteForm.banque} onChange={e => setCompteForm(f => ({ ...f, banque: e.target.value }))} placeholder="Crédit Mutuel, BNP…" />
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="sm">IBAN</FormLabel>
                                <Input value={compteForm.iban} onChange={e => setCompteForm(f => ({ ...f, iban: e.target.value }))} placeholder="FR76…" fontFamily="mono" />
                            </FormControl>
                            <SimpleGrid columns={2} spacing={3} w="full">
                                <FormControl>
                                    <FormLabel fontSize="sm">Compte PCG</FormLabel>
                                    <Input value={compteForm.compte_comptable} onChange={e => setCompteForm(f => ({ ...f, compte_comptable: e.target.value }))} placeholder="512" />
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="sm">Solde initial (€)</FormLabel>
                                    <Input type="number" step="0.01" value={compteForm.solde_initial} onChange={e => setCompteForm(f => ({ ...f, solde_initial: e.target.value }))} placeholder="0.00" />
                                </FormControl>
                            </SimpleGrid>
                            <FormControl>
                                <FormLabel fontSize="sm">Date d'ouverture</FormLabel>
                                <Input type="date" value={compteForm.date_ouverture} onChange={e => setCompteForm(f => ({ ...f, date_ouverture: e.target.value }))} />
                            </FormControl>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onCompteClose}>Annuler</Button>
                        <Button colorScheme="blue" onClick={saveCompte}>Enregistrer</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </VStack>
    );

    const renderImport = () => (
        <VStack align="stretch" spacing={6}>
            {canRapprocher && (
                <Card size="sm">
                    <CardHeader><Heading size="sm">📂 Importer un relevé bancaire (CSV)</Heading></CardHeader>
                    <CardBody>
                        <VStack spacing={3} align="stretch">
                            <Alert status="info" fontSize="sm">
                                <AlertIcon />
                                Formats acceptés : CSV avec colonnes Date, Libellé, Débit, Crédit (séparateur ; ou ,). La plupart des banques françaises exportent dans ce format.
                            </Alert>
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                                <FormControl isRequired>
                                    <FormLabel fontSize="sm">Compte bancaire</FormLabel>
                                    <Select value={uploadForm.compte_id} onChange={e => setUploadForm(f => ({ ...f, compte_id: e.target.value }))} placeholder="Choisir un compte…">
                                        {comptes.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                                    </Select>
                                </FormControl>
                                <FormControl isRequired>
                                    <FormLabel fontSize="sm">Fichier CSV</FormLabel>
                                    <Input type="file" accept=".csv,.txt" ref={fileRef} />
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="sm">Solde initial du relevé (€)</FormLabel>
                                    <Input type="number" step="0.01" value={uploadForm.solde_initial} onChange={e => setUploadForm(f => ({ ...f, solde_initial: e.target.value }))} placeholder="Optionnel" />
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="sm">Solde final du relevé (€)</FormLabel>
                                    <Input type="number" step="0.01" value={uploadForm.solde_final} onChange={e => setUploadForm(f => ({ ...f, solde_final: e.target.value }))} placeholder="Optionnel" />
                                </FormControl>
                            </SimpleGrid>
                            <Button leftIcon={<FiUpload />} colorScheme="blue" onClick={handleImport} isLoading={uploading} alignSelf="flex-start">
                                Importer
                            </Button>
                        </VStack>
                    </CardBody>
                </Card>
            )}

            <Heading size="sm">Relevés importés</Heading>
            <HStack>
                <Select value={selectedCompteId} onChange={e => { setSelectedCompteId(e.target.value); loadReleves(e.target.value); }} maxW="250px" placeholder="Tous les comptes">
                    {comptes.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                </Select>
            </HStack>
            <Box overflowX="auto">
                <Table size="sm">
                    <Thead bg="gray.50">
                        <Tr><Th>Fichier</Th><Th>Compte</Th><Th>Période</Th><Th isNumeric>Lignes</Th><Th isNumeric>Lettrées</Th><Th isNumeric>Non lettrées</Th><Th>Importé le</Th>{canRapprocher && <Th />}</Tr>
                    </Thead>
                    <Tbody>
                        {releves.length === 0 && <Tr><Td colSpan={8} textAlign="center" color="gray.400" py={6}>Aucun relevé importé</Td></Tr>}
                        {releves.map(r => (
                            <Tr key={r.id} _hover={{ bg: 'gray.50' }}>
                                <Td fontSize="sm">{r.nom_fichier}</Td>
                                <Td fontSize="sm">{r.compte_nom}</Td>
                                <Td fontSize="xs" color="gray.500">{r.date_debut} → {r.date_fin}</Td>
                                <Td isNumeric>{r.nb_lignes}</Td>
                                <Td isNumeric><Badge colorScheme="green">{r.nb_lettrees || 0}</Badge></Td>
                                <Td isNumeric><Badge colorScheme={parseInt(r.nb_non_lettrees) > 0 ? 'orange' : 'gray'}>{r.nb_non_lettrees || 0}</Badge></Td>
                                <Td fontSize="xs" color="gray.500">{r.date_import?.split('T')[0]}</Td>
                                {canRapprocher && (
                                    <Td>
                                        <HStack spacing={1}>
                                            <Tooltip label="Rapprocher">
                                                <IconButton size="xs" icon={<FiLink />} variant="ghost" colorScheme="blue"
                                                    onClick={() => { setSelectedReleveId(String(r.id)); setSection('rapprochement'); loadRapprochement(String(r.id)); }}
                                                    aria-label="Rapprocher" />
                                            </Tooltip>
                                            {canWrite && (
                                                <IconButton size="xs" icon={<FiTrash2 />} variant="ghost" colorScheme="red"
                                                    onClick={async () => { if (window.confirm('Supprimer ce relevé et toutes ses lignes ?')) { await apiService.makeRequest(`/banque/releves/${r.id}`, { method: 'DELETE' }); loadReleves(selectedCompteId); } }}
                                                    aria-label="Supprimer" />
                                            )}
                                        </HStack>
                                    </Td>
                                )}
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </Box>
        </VStack>
    );

    const renderRapprochement = () => (
        <VStack align="stretch" spacing={4}>
            <HStack flexWrap="wrap">
                <Select value={selectedReleveId} onChange={e => { setSelectedReleveId(e.target.value); loadRapprochement(e.target.value); }} maxW="350px" placeholder="Choisir un relevé…">
                    {releves.map(r => <option key={r.id} value={r.id}>{r.compte_nom} — {r.date_debut} → {r.date_fin} ({r.nb_lignes} lignes)</option>)}
                </Select>
                {selectedReleveId && <Button size="sm" leftIcon={<FiRefreshCw />} onClick={() => loadRapprochement(selectedReleveId)} isLoading={loadingRap}>Actualiser</Button>}
            </HStack>

            {resume && (
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                    <Stat bg="gray.50" p={3} borderRadius="md">
                        <StatLabel fontSize="xs">Total lignes</StatLabel>
                        <StatNumber fontSize="lg">{resume.total}</StatNumber>
                    </Stat>
                    <Stat bg="green.50" p={3} borderRadius="md">
                        <StatLabel fontSize="xs" color="green.700">Lettrées</StatLabel>
                        <StatNumber fontSize="lg" color="green.700">{resume.lettrees}</StatNumber>
                    </Stat>
                    <Stat bg="orange.50" p={3} borderRadius="md">
                        <StatLabel fontSize="xs" color="orange.700">Non lettrées</StatLabel>
                        <StatNumber fontSize="lg" color="orange.700">{resume.non_lettrees}</StatNumber>
                    </Stat>
                    <Stat bg="blue.50" p={3} borderRadius="md">
                        <StatLabel fontSize="xs" color="blue.700">Écart non lettré</StatLabel>
                        <StatNumber fontSize="sm" color="blue.700">{formatMoney(resume.ecart_non_lettre)}</StatNumber>
                    </Stat>
                </SimpleGrid>
            )}

            {loadingRap && <Flex justify="center"><Spinner /></Flex>}

            {rapprochement && (
                <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
                    {/* Lignes du relevé */}
                    <Box>
                        <Heading size="xs" mb={2} color="gray.600">Lignes du relevé bancaire</Heading>
                        <Box overflowX="auto" maxH="500px" overflowY="auto">
                            <Table size="xs" variant="simple">
                                <Thead position="sticky" top={0} bg="white" zIndex={1}>
                                    <Tr><Th>Date</Th><Th>Libellé</Th><Th isNumeric>Débit</Th><Th isNumeric>Crédit</Th><Th>État</Th>{canRapprocher && <Th />}</Tr>
                                </Thead>
                                <Tbody>
                                    {rapprochement.lignes.map(l => {
                                        const isSelected = selectedLigneId === l.id;
                                        const suggestion = rapprochement.suggestions.find(s => s.ligne_id === l.id);
                                        return (
                                            <Tr key={l.id}
                                                bg={l.statut === 'lettree' ? 'green.50' : l.statut === 'ignoree' ? 'gray.50' : isSelected ? 'blue.50' : 'white'}
                                                cursor={l.statut === 'non_lettree' && canRapprocher ? 'pointer' : 'default'}
                                                onClick={() => l.statut === 'non_lettree' && canRapprocher && setSelectedLigneId(isSelected ? null : l.id)}
                                                _hover={{ bg: l.statut === 'non_lettree' ? 'blue.50' : undefined }}>
                                                <Td whiteSpace="nowrap" fontSize="xs">{l.date_operation}</Td>
                                                <Td fontSize="xs" maxW="160px">
                                                    <Text noOfLines={1} title={l.libelle}>{l.libelle}</Text>
                                                    {suggestion && l.statut === 'non_lettree' && (
                                                        <Badge colorScheme="purple" fontSize="xx-small">💡 suggestion</Badge>
                                                    )}
                                                </Td>
                                                <Td isNumeric fontSize="xs" color="red.600">{parseFloat(l.debit) > 0 ? formatMoney(l.debit) : ''}</Td>
                                                <Td isNumeric fontSize="xs" color="green.600">{parseFloat(l.credit) > 0 ? formatMoney(l.credit) : ''}</Td>
                                                <Td>
                                                    <Badge fontSize="xx-small" colorScheme={l.statut === 'lettree' ? 'green' : l.statut === 'ignoree' ? 'gray' : 'orange'}>
                                                        {l.statut === 'lettree' ? '✓' : l.statut === 'ignoree' ? '–' : '?'}
                                                    </Badge>
                                                </Td>
                                                {canRapprocher && (
                                                    <Td>
                                                        {l.statut === 'lettree' && (
                                                            <Tooltip label="Délettrer"><IconButton size="xs" icon={<FiXCircle />} variant="ghost" colorScheme="orange" onClick={e => { e.stopPropagation(); delettrer(l.id); }} aria-label="Délettrer" /></Tooltip>
                                                        )}
                                                        {l.statut === 'non_lettree' && (
                                                            <Tooltip label="Ignorer"><IconButton size="xs" icon={<FiMinus />} variant="ghost" colorScheme="gray" onClick={e => { e.stopPropagation(); ignorer(l.id); }} aria-label="Ignorer" /></Tooltip>
                                                        )}
                                                    </Td>
                                                )}
                                            </Tr>
                                        );
                                    })}
                                </Tbody>
                            </Table>
                        </Box>
                        {selectedLigneId && <Text fontSize="xs" color="blue.600" mt={1}>Ligne sélectionnée — cliquez une écriture à droite pour l'associer</Text>}
                    </Box>

                    {/* Écritures comptables */}
                    <Box>
                        <Heading size="xs" mb={2} color="gray.600">Écritures comptables disponibles</Heading>
                        <Box overflowX="auto" maxH="500px" overflowY="auto">
                            <Table size="xs" variant="simple">
                                <Thead position="sticky" top={0} bg="white" zIndex={1}>
                                    <Tr><Th>Date</Th><Th>Libellé</Th><Th isNumeric>Débit</Th><Th isNumeric>Crédit</Th></Tr>
                                </Thead>
                                <Tbody>
                                    {rapprochement.ecritures.length === 0 && (
                                        <Tr><Td colSpan={4} textAlign="center" color="gray.400" py={4}>Aucune écriture disponible sur cette période</Td></Tr>
                                    )}
                                    {rapprochement.ecritures.map(e => {
                                        const suggestion = rapprochement.suggestions
                                            .find(s => s.candidates.some(c => c.id === e.id));
                                        return (
                                            <Tr key={e.id}
                                                bg={suggestion ? 'purple.50' : 'white'}
                                                cursor={selectedLigneId && canRapprocher ? 'pointer' : 'default'}
                                                _hover={{ bg: selectedLigneId ? 'blue.100' : undefined }}
                                                onClick={() => selectedLigneId && canRapprocher && lettrer(selectedLigneId, e.id)}>
                                                <Td whiteSpace="nowrap" fontSize="xs">{e.date_ecriture}</Td>
                                                <Td fontSize="xs" maxW="160px">
                                                    <Text noOfLines={1} title={e.libelle}>{e.libelle}</Text>
                                                    {suggestion && <Badge colorScheme="purple" fontSize="xx-small">💡</Badge>}
                                                </Td>
                                                <Td isNumeric fontSize="xs" color="red.600">{parseFloat(e.debit) > 0 ? formatMoney(e.debit) : ''}</Td>
                                                <Td isNumeric fontSize="xs" color="green.600">{parseFloat(e.credit) > 0 ? formatMoney(e.credit) : ''}</Td>
                                            </Tr>
                                        );
                                    })}
                                </Tbody>
                            </Table>
                        </Box>
                    </Box>
                </SimpleGrid>
            )}
        </VStack>
    );

    return (
        <VStack align="stretch" spacing={4}>
            <HStack spacing={1} borderBottom="2px solid" borderColor="gray.100" pb={2}>
                {[
                    { key: 'comptes', label: '🏦 Comptes' },
                    { key: 'import',  label: '📂 Import relevés' },
                    { key: 'rapprochement', label: '🔗 Rapprochement' },
                ].map(s => (
                    <Button key={s.key} size="sm" variant={section === s.key ? 'solid' : 'ghost'}
                        colorScheme={section === s.key ? 'blue' : 'gray'}
                        onClick={() => setSection(s.key)}>
                        {s.label}
                    </Button>
                ))}
            </HStack>
            {section === 'comptes'         && renderComptes()}
            {section === 'import'          && renderImport()}
            {section === 'rapprochement'   && renderRapprochement()}
        </VStack>
    );
}

// ─────────────────────────────────────────────────────────────
// PAGE PRINCIPALE
// ─────────────────────────────────────────────────────────────
export default function Comptabilite() {
    const { user: currentUser } = useAuth();
    const [exercices, setExercices] = useState([]);
    const [planComptable, setPlanComptable] = useState([]);
    const [loadingInit, setLoadingInit] = useState(true);
    const toast = useToast();

    useEffect(() => {
        const init = async () => {
            try {
                const [exData, planData] = await Promise.all([
                    apiService.makeRequest('/comptabilite/exercices'),
                    apiService.makeRequest('/comptabilite/plan'),
                ]);
                setExercices(exData.data);
                setPlanComptable(planData.data);
            } catch (e) {
                toast({ title: 'Erreur de chargement', description: e.message, status: 'error', duration: 4000 });
            } finally { setLoadingInit(false); }
        };
        init();
    }, [toast]);

    if (loadingInit) return <Flex justify="center" pt={20}><Spinner size="xl" /></Flex>;

    return (
        <Box>
            <Flex align="center" mb={6}>
                <Box>
                    <Heading size="lg">Comptabilité</Heading>
                    <Text color="gray.500" fontSize="sm">Plan Comptable Général — Associations (Loi 1901)</Text>
                </Box>
                <Spacer />
                {exercices.length > 0 && (
                    <Badge colorScheme={exercices[0]?.is_cloture ? 'gray' : 'green'} fontSize="sm" px={3} py={1}>
                        Exercice {exercices[0]?.annee} {exercices[0]?.is_cloture ? '(clôturé)' : '(en cours)'}
                    </Badge>
                )}
            </Flex>

            <Tabs colorScheme="blue" isLazy>
                <TabList overflowX="auto">
                    <Tab>📊 Compte de résultat</Tab>
                    <Tab>⚖️ Balance</Tab>
                    <Tab>📒 Grand Livre</Tab>
                    <Tab>📝 Journal</Tab>
                    <Tab>🔬 Analytique</Tab>
                    <Tab>📈 Rapports</Tab>
                    <Tab>🏦 Banque</Tab>
                </TabList>
                <TabPanels>
                    <TabPanel>
                        <CompteResultatTab exercices={exercices} />
                    </TabPanel>
                    <TabPanel>
                        <BalanceTab exercices={exercices} />
                    </TabPanel>
                    <TabPanel>
                        <GrandLivreTab exercices={exercices} planComptable={planComptable} />
                    </TabPanel>
                    <TabPanel>
                        <JournalTab exercices={exercices} planComptable={planComptable} currentUser={currentUser} />
                    </TabPanel>
                    <TabPanel>
                        <AnalytiqueTab exercices={exercices} currentUser={currentUser} />
                    </TabPanel>
                    <TabPanel>
                        <RapportsTab exercices={exercices} />
                    </TabPanel>
                    <TabPanel>
                        <BanqueTab currentUser={currentUser} />
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </Box>
    );
}
